import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, ElementRef, Inject, Optional, Renderer2, HostListener, TemplateRef, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { EntityManagementComponent as ParentEntityManagementComponent } from '@shared-lib/chart-components/entity-management/entity-management.component';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '@shared-lib/shared-services/environment-config.token';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { ResponsiveLayoutService } from '@shared-lib/shared-services/responsive-layout.service';
import { KeycloakService } from 'keycloak-angular';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { catchError, concatMap, finalize, from, map, Observable, of, shareReplay, Subscription, switchMap, tap, toArray } from 'rxjs';
import {
  mapRerAuditSourceData,
  RER_AUDIT_ENTITY,
  RER_AUDIT_SOURCE_FIELDS,
  RER_PA_HISTORY_ENTITIES,
} from '../rer-audit-record.utils';

@Component({
  selector: 'app-entity-management',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatSelectModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    NgxMatSelectSearchModule,
  ],
  templateUrl: './entity-management.component.html',
  styleUrls: ['./entity-management.component.scss'],
})
export class EntityManagementComponent extends ParentEntityManagementComponent {
  private static readonly RER_ENGINE_PRICING_ENTITY_KEYS = new Set([
    'custom_rer_engine_pricing',
    'cu_rer_engine_pricing',
    'rer engine pricing',
  ]);
  private static readonly RER_NEWEST_FIRST_ENTITY_KEYS = new Set([
    ...EntityManagementComponent.RER_ENGINE_PRICING_ENTITY_KEYS,
    'custom_rer_suggested',
    'cu_suggested_prices_rer_engine',
    'rer suggested price',
  ]);
  private static readonly QUOTE_INTEGRATION_ENTITY_KEYS = new Set([
    'cummins_quote',
    'cummins_quote_detail',
    'cummins_bill_to_ship_to_config',
    'cummins_intercompany_configuration',
  ]);
  private static readonly GOMS_CUSTOMER_ENTITY_KEYS = new Set([
    'cu_supp_s_goms_customer',
    's_goms_customer',
    's_goms customer',
  ]);
  private static readonly SERVER_PAGINATED_ENTITY_KEYS = new Set([
    'custom_rer_engine_pricing',
    'custom_rer_suggested',
    RER_AUDIT_ENTITY,
    'cummins_quote',
    'cummins_quote_detail',
    'cummins_bill_to_ship_to_config',
    'cummins_intercompany_configuration',
    'cu_supp_s_goms_customer',
  ]);
  private static readonly SELECTION_COLUMN_WIDTH = 24;
  private static readonly SETTINGS_STORAGE_PREFIX = 'palantir-entity-table-settings';
  private static readonly QUOTE_DETAIL_ENTITY = 'cummins_quote_detail';
  private static readonly QUOTE_DETAIL_FUNCTION = 'GenericDynamicReadServices';
  private static readonly DEFAULT_DYNAMIC_READ_FUNCTION = 'GenericDynamicReadServices';
  private static readonly RER_AUDIT_ENTITY = RER_AUDIT_ENTITY;
  private static readonly RER_PA_HISTORY_FIELD_TOKENS = new Set(
    RER_AUDIT_SOURCE_FIELDS.map((field) => field.replace(/_/g, ' ')),
  );
  private static readonly RER_AUDIT_API_URL = '/spriced/platform/getData';
  private static readonly DEBUG_LOG_KEY = 'PALANTIR_CONFIG_TABLE_DEBUG_V3';
  private static readonly EYE_LOOP_DEBUG_KEY = 'QUOTE_EYE_LOOP_DEBUG_2026_08_17';
  private static readonly TABLE_REFRESH_EVENT = 'palantir-table-refresh-request';
  private static readonly FORM_SCHEMA_EVENT = 'palantir-form-schema-change';
  private static readonly QUOTE_FORM_POPUP_EVENT = 'palantir-quote-form-popup-request';
  private static readonly LAST_CREATED_RECORD_STORAGE_PREFIX = 'palantir-last-created-record';
  private static readonly COLUMN_SCHEMA_SUFFIX = '_columns';
  private static readonly ALWAYS_HIDDEN_FIELD_TOKENS = new Set(['cpm']);
  private static readonly HIDDEN_FIELD_TOKENS = new Set(['is valid', 'created by', 'created date', 'updated by', 'updated date', 'uuid', 'cpm']);
  private static readonly ENTITY_HIDDEN_FIELD_TOKENS = new Map<string, Set<string>>([
    [RER_AUDIT_ENTITY, new Set(['region', 'regional pricing flag'])],
    ['custom_rer_suggested', new Set(['is deleted'])],
    ['cu_suggested_prices_rer_engine', new Set(['is deleted'])],
  ]);
  private static readonly RER_PA_HISTORY_COLUMN_ORDER = new Map([
    ['code', 0],
    ['pricing action', 1],
    ['change reason', 2],
    ['old price', 3],
    ['suggested future price', 4],
    ['current price', 5],
    ['current effective date', 6],
    ['notes', 7],
  ]);
  private static readonly PREFERRED_COLUMN_ORDER = [
    'name',
    'code',
    'goms',
    'quote year',
    'pricing region',
    'quote region',
    'product bu',
    'pricing admin owner',
    'bill tos',
    'sap bill to code ship to',
    'parts manager',
    'perform impact',
    'quote status',
    'link to impact report',
    'impact run date',
    'final impact',
    'get file',
    'link to goms outbound file',
    'submit for approval',
    'has waiver approved',
    'regional marketing leader approval',
    'local finance marketing controller approval',
    'account number',
    'regional director or manager approval',
    'global parts marketing director approval',
    'finance director approval',
    'exec director approval',
    'exec finance director approval',
    'vice president approval',
    'president or director approval',
    'approvals status',
    'status',
    'publish to sap date',
    'sap resend flag',
    'resend for approval',
    'denial reason',
    'link to request',
    'notes',
    'static units',
    'dynamic units',
    'final marketing impact',
  ];
  private readonly customGlobalFilterService: GlobalFilterService;
  private readonly customConfigService: ConfigService;
  private readonly route: ActivatedRoute;
  private readonly router: Router;
  private readonly environmentConfig: EnvironmentConfig | null;
  private readonly http: HttpClient;
  private readonly serverLastPageByEntity = new Map<string, number>();
  private readonly requestedEntityCounts = new Set<string>();
  private readonly entityRowCounts = new Map<string, number>();
  private tableDefaultFunctionName = '';
  private tableEntityFunctionNameOverrides: Record<string, string> | null = null;
  private lastCreatedRecordSnapshot: any = null;
  private lastSavedRecordSnapshot: any = null;
  private tableRefreshEventListener?: EventListener;
  private formSchemaEventListener?: EventListener;
  private quoteFormPopupEventListener?: EventListener;
  private domainAttributeColumnKeys = new Set<string>();
  private domainValueLookupByColumn = new Map<
    string,
    Map<string, { code: string; name: string }>
  >();
  private headerOrderCache = new Map<string, Observable<string[] | null>>();
  private initialTableSettings?: {
    pageSize: number;
    frozenColumns: number;
    displayFormat: string;
    showSystemAttribute: boolean;
  };
  private eyeLoopDebugCounter = 0;
  private eyeLoopErrorListener?: EventListener;
  private eyeLoopUnhandledRejectionListener?: EventListener;
  private activeCellDialogRecord: any = null;
  private entityPermissionsRequest?: Subscription;

  // Custom download menu state
  showDownloadMenu = false;
  downloadMenuTop = 0;
  downloadMenuRight = 0;
  currentDownloadAction: any = null;
  @ViewChild('downloadConfirmDialog') downloadConfirmDialog?: TemplateRef<any>;
  @ViewChild('deleteDialog') deleteDialogTpl?: TemplateRef<any>;
  @ViewChild('headerUploadInput') headerUploadInput?: ElementRef<HTMLInputElement>;
  private deleteDialogRef?: any;
  private pendingUploadAction: any = null;

  // Quote approval-status dialog state
  @ViewChild('approvalStatusDialog') approvalStatusDialog?: TemplateRef<any>;
  approvalRows: Array<{
    date: string;
    level: number | string;
    user: string;
    email: string;
    status: string;
  }> = [];
  approvalLoading = false;
  approvalLoadError = '';
  approvalColumns = [
    { key: 'date', label: 'Date' },
    { key: 'level', label: 'Approval Name' },
    { key: 'user', label: 'User' },
    { key: 'email', label: 'Email' },
    { key: 'status', label: 'Status' },
    { key: 'actions', label: 'Actions' },
  ];
  draggedApprovalColumnKey: string | null = null;
  approvalDropTargetKey: string | null = null;
  private approvalDialogRef?: any;

  // View Transactions (history) dialog state
  @ViewChild('historyDialog') historyDialog?: TemplateRef<any>;
  historyAction: any = null;
  historyRecords: any[] = [];
  historyLoading = false;
  historyPage = 1;
  historyPageSize = 10;
  historySortKey: string | null = null;
  historySortDirection: 'asc' | 'desc' = 'asc';
  historyAnnotations: string[] = [];
  newHistoryAnnotation = '';
  activeCellDialogEmptyRows: number[] = [];
  cellDialogSaving = false;
  cellDialogSaveError = '';

  toggleDownloadMenu(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.showDownloadMenu) {
      const btn = event.currentTarget as HTMLElement;
      const rect = btn.getBoundingClientRect();
      this.downloadMenuTop = rect.bottom + 4;
      this.downloadMenuRight = window.innerWidth - rect.right;
    }
    this.showDownloadMenu = !this.showDownloadMenu;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const downloadContainer = this.elementRef?.nativeElement?.querySelector('.download-container');
    if (downloadContainer && downloadContainer.contains(target)) {
      return;
    }
    this.showDownloadMenu = false;
  }

  openDeleteConfirmation(): void {
    if (this.getSelectedRecordsForAction().length === 0) return;
    if (this.deleteDialogTpl && this.dialog) {
      this.deleteDialogRef = this.dialog.open(this.deleteDialogTpl, {
        width: '320px',
        panelClass: 'custom-download-dialog-panel'
      });
    }
  }

  closeDeleteDialog(confirmed: boolean): void {
    if (this.deleteDialogRef) {
      this.deleteDialogRef.close();
      this.deleteDialogRef = undefined;
    }
    if (confirmed) {
      this.deleteSelectedRecords();
    }
  }

  protected override deleteSelectedRecords(): void {
    if (this.resolveSelectedEntityKey().trim().toLowerCase() !== 'custom_rer_engine_pricing') {
      super.deleteSelectedRecords();
      return;
    }

    const selectedRecords = this.getSelectedRecordsForAction();
    if (selectedRecords.length === 0) {
      return;
    }

    const deleteRequests = selectedRecords.map((record) => this.buildRerEnginePricingDeletePayload(record));
    if (deleteRequests.some((payload) => !payload)) {
      this.showConfiguredActionMessage(
        'Unable to delete: the selected row is missing a required business key.',
        'error-snackbar',
      );
      return;
    }

    const apiUrl = this.customConfigService.resolveUrl('/spriced/platform');
    from(deleteRequests).pipe(
      concatMap((payload) => this.http.post(apiUrl, payload)),
      toArray(),
      switchMap(() => {
        this.selection.clear();
        this.allSelected = false;
        this.selectRecord(null);
        (this as any).pendingDialogRequestData = this.buildRerEnginePricingFetchPayload();
        return this.loadRecords();
      }),
    ).subscribe({
      next: () => this.showConfiguredActionMessage(
        selectedRecords.length === 1
          ? 'RER engine pricing row deleted successfully.'
          : `${selectedRecords.length} RER engine pricing rows deleted successfully.`,
        'success-snackbar',
      ),
      error: (error) => {
        console.error('Error deleting RER engine pricing row:', error);
        this.showConfiguredActionMessage('Unable to delete the RER engine pricing row.', 'error-snackbar');
      },
    });
  }

  private buildRerEnginePricingDeletePayload(record: any): any | null {
    const technicalConfiguration = this.resolvePricingConfigValue(record, 'technical_configuration');
    const marketingConfiguration = this.resolvePricingConfigValue(record, 'marketing_configuration');
    // The delete contract represents a missing fuel-rating business-key value
    // with the literal EMPTY token. Keep this conversion in the entity-specific
    // delete builder so create/update payloads and every other field/entity keep
    // their existing null/blank handling.
    const fuelRatingHp = this.resolvePricingConfigValue(record, 'fuel_rating_hp') ?? 'EMPTY';

    if (!technicalConfiguration || !marketingConfiguration) {
      return null;
    }

    return {
      requestData: {
        technical_configuration: technicalConfiguration,
        marketing_configuration: marketingConfiguration,
        fuel_rating_hp: fuelRatingHp,
        is_deleted: true,
        operation: 'delete',
        businessKey: [
          'technical_configuration',
          'marketing_configuration',
          'fuel_rating_hp',
        ],
      },
      responseList: [],
      entity: 'custom_rer_engine_pricing',
      batchId: 'test',
      workflowId: 'ABC-123',
      endpoints: [],
      version: '1.0',
    };
  }

  private buildRerEnginePricingFetchPayload(offset = 0): any {
    return {
      requestData: {
        entity: 'custom_rer_engine_pricing',
        is_deleted: false,
        filters: [
          {
            attributeName: 'uuid',
            operator: 'is_not_null',
            attributeValue: null,
            attributeType: 'string',
            logicalOperator: 'AND',
            beginScope: '',
            endScope: '',
          },
          {
            attributeName: 'is_deleted',
            operator: '=',
            attributeValue: false,
            attributeType: 'boolean',
            logicalOperator: '',
            beginScope: '',
            endScope: '',
          },
          {
            attributeName: 'limit',
            operator: 'is',
            logicalOperator: '',
            beginScope: '',
            endScope: '',
            attributeValue: this.pageSize,
          },
          {
            attributeName: 'offset',
            operator: 'is',
            logicalOperator: '',
            beginScope: '',
            endScope: '',
            attributeValue: offset,
          },
          {
            attributeName: 'order by',
            operator: 'is',
            logicalOperator: '',
            beginScope: '',
            endScope: '',
            attributeValue: 'created_date desc',
          },
        ],
      },
      responseList: [],
      entity: 'product',
      workflowId: 'UI',
      endpoints: [
        {
          functionName: 'CuRERSuggestPrice',
          version: '1.0',
        },
      ],
      communicationType: 'rest',
      version: '1.0',
    };
  }

  /** Use the RER pricing read contract for this entity without affecting other table entities. */
  private prepareRerEnginePricingFetchPayload(): void {
    const selectedEntity = this.resolveSelectedEntityKey().trim().toLowerCase();
    if (selectedEntity !== 'custom_rer_engine_pricing') {
      return;
    }

    if (!(this as any).pendingDialogRequestData) {
      (this as any).pendingDialogRequestData = this.buildRerEnginePricingFetchPayload();
    }

    // Filter-dialog and pagination payloads are explicit requests, so they do
    // not use buildRerEnginePricingFetchPayload(). Enforce the same active-row
    // constraints on those requests and on the stored filter used by refresh.
    this.ensureRerEnginePricingActiveFilters(
      (this as any).pendingDialogRequestData,
      this.hasDialogFilterApplied,
    );
    this.ensureRerEnginePricingActiveFilters((this as any).appliedDialogFilterPayload, true);
  }

  private buildRerAuditFetchPayload(offset = 0): any {
    return {
      attributesList: [
        'deam_id',
        'source_data',
      ],
      filterList: [
        {
          operator: 'is not',
          attributeName: 'uuid',
          attributeValue: 'null',
          logicalOperator: 'AND',
        },
        {
          operator: '=',
          attributeName: 'request_type',
          attributeValue: 'price_change',
          logicalOperator: '',
        },
        {
          attributeName: 'limit',
          operator: 'is',
          logicalOperator: '',
          attributeValue: this.pageSize,
        },
        {
          attributeName: 'offset',
          operator: 'is',
          logicalOperator: '',
          attributeValue: offset,
        },
        {
          attributeName: 'order by',
          operator: 'is',
          logicalOperator: '',
          attributeValue: 'uuid desc',
        },
      ],
      entityName: EntityManagementComponent.RER_AUDIT_ENTITY,
    };
  }

  /** Apply the getData contract only to the RER Audit table request. */
  private prepareRerAuditFetchPayload(): void {
    if (this.resolveSelectedEntityKey().trim().toLowerCase() !== EntityManagementComponent.RER_AUDIT_ENTITY) {
      return;
    }

    const currentOffset = this.getServerRequestOffset(
      (this as any).pendingDialogRequestData || this.data?.customPayload,
    );
    (this as any).pendingDialogRequestData = this.buildRerAuditFetchPayload(currentOffset);
  }

  private loadTableRecordsForSelectedEntity(): Observable<any[]> {
    if (this.resolveSelectedEntityKey().trim().toLowerCase() !== EntityManagementComponent.RER_AUDIT_ENTITY) {
      return super.loadRecords();
    }

    const configuredApiUrl = this.data.apiUrl;
    this.data.apiUrl = EntityManagementComponent.RER_AUDIT_API_URL;
    try {
      return super.loadRecords();
    } finally {
      this.data.apiUrl = configuredApiUrl;
    }
  }

  private ensureRerEnginePricingActiveFilters(
    payload: any,
    connectToAppliedFilter = false,
  ): void {
    const requestData = payload?.requestData;
    if (!requestData || typeof requestData !== 'object') {
      return;
    }

    requestData.is_deleted = false;

    const configuredFilters = Array.isArray(requestData.filters) ? requestData.filters : [];
    const remainingFilters = configuredFilters.filter((filter: any) => {
      const attributeName = String(filter?.attributeName || '').trim().toLowerCase();
      return attributeName !== 'uuid' && attributeName !== 'is_deleted';
    });

    requestData.filters = [
      {
        attributeName: 'uuid',
        operator: 'is_not_null',
        attributeValue: null,
        attributeType: 'string',
        logicalOperator: 'AND',
        beginScope: '',
        endScope: '',
      },
      {
        attributeName: 'is_deleted',
        operator: '=',
        attributeValue: false,
        attributeType: 'boolean',
        logicalOperator: connectToAppliedFilter ? 'AND' : '',
        beginScope: '',
        endScope: '',
      },
      ...remainingFilters,
    ];
  }

  openDownloadConfirmation(action: any): void {
    this.showDownloadMenu = false;
    this.currentDownloadAction = action;
    if (this.downloadConfirmDialog && this.dialog) {
      this.dialogRef = this.dialog.open(this.downloadConfirmDialog, {
        width: '320px',
        panelClass: 'custom-download-dialog-panel'
      });
    }
  }

  closeDialog(save: boolean, action?: any): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
    if (save && action) {
      if (action.actionId === 'download' && this.getEntityActionConfig(action, action?.downloadApi)?.apiUrl) {
        this.downloadConfiguredFile(action);
      } else {
        super.handleHeaderAction(action.actionId, action);
      }
    }
    this.currentDownloadAction = null;
  }

  get historyColumns(): any[] {
    return Array.isArray(this.historyAction?.tableColumns) ? this.historyAction.tableColumns : [];
  }

  get historyDialogTitle(): string {
    const baseTitle = this.historyAction?.dialogTitle || 'View Transactions';
    const labelKeys: string[] = Array.isArray(this.historyAction?.recordLabelKeys)
      ? this.historyAction.recordLabelKeys
      : ['code', 'name'];
    const record = this.selectedRecord || {};
    const recordLabel = labelKeys
      .map((key) => record?.[key])
      .filter((value) => value !== null && value !== undefined && String(value).trim() !== '')
      .join(' ');
    return recordLabel ? `${baseTitle} for ${recordLabel}` : baseTitle;
  }

  get historyEmptyMessage(): string {
    return this.historyAction?.emptyStateMessage || 'No data to display';
  }

  get pagedHistoryRecords(): any[] {
    const start = (this.historyPage - 1) * this.historyPageSize;
    return this.historyRecords.slice(start, start + this.historyPageSize);
  }

  openHistoryDialog(action: any): void {
    this.historyAction = action || {};
    this.historyRecords = [];
    this.historyPage = 1;
    this.historyPageSize = Number(action?.pageSize) > 0 ? Number(action.pageSize) : 10;
    this.historySortKey = null;
    this.historySortDirection = 'asc';
    this.historyAnnotations = [];
    this.newHistoryAnnotation = '';
    this.loadHistoryRecords(action);

    if (this.historyDialog && this.dialog) {
      this.dialogRef = this.dialog.open(this.historyDialog, {
        width: action?.dialogWidth || '78vw',
        maxWidth: action?.dialogMaxWidth || '90vw',
        panelClass: 'custom-download-dialog-panel',
      });
    }
  }

  closeHistoryDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
    this.historyAction = null;
  }

  sortHistoryBy(key: string): void {
    if (this.historySortKey === key) {
      this.historySortDirection = this.historySortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.historySortKey = key;
      this.historySortDirection = 'asc';
    }

    const direction = this.historySortDirection === 'asc' ? 1 : -1;
    this.historyRecords = [...this.historyRecords].sort((a, b) => {
      const left = a?.[key];
      const right = b?.[key];
      if (left === right) {
        return 0;
      }
      if (left === null || left === undefined) {
        return -direction;
      }
      if (right === null || right === undefined) {
        return direction;
      }
      return String(left).localeCompare(String(right), undefined, { numeric: true }) * direction;
    });
    this.historyPage = 1;
  }

  addHistoryAnnotation(): void {
    const annotation = (this.newHistoryAnnotation || '').trim();
    if (!annotation) {
      return;
    }
    this.historyAnnotations = [...this.historyAnnotations, annotation];
    this.newHistoryAnnotation = '';
  }

  private loadHistoryRecords(action: any): void {
    const apiUrl = action?.apiUrl;
    if (!apiUrl) {
      return;
    }

    this.historyLoading = true;
    const method = String(action?.method || 'GET').toUpperCase();
    const request$ = method === 'POST'
      ? this.http.post(apiUrl, this.buildHistoryPayload(action))
      : this.http.get(apiUrl);

    request$
      .pipe(catchError(() => of(null)))
      .subscribe((response: any) => {
        this.historyRecords = this.extractHistoryRows(response);
        this.historyLoading = false;
      });
  }

  private buildHistoryPayload(action: any): any {
    const payload = JSON.parse(JSON.stringify(action?.customPayload || {}));
    const record = this.selectedRecord || {};
    const replaceTokens = (value: any): any => {
      if (typeof value === 'string') {
        return value
          .replace(/\{\{\s*selectedRecordId\s*\}\}/g, String(record?.id ?? ''))
          .replace(/\{\{\s*entity\s*\}\}/g, String(this.currentFilters?.['entity'] ?? ''));
      }
      if (Array.isArray(value)) {
        return value.map(replaceTokens);
      }
      if (value && typeof value === 'object') {
        Object.keys(value).forEach((key) => {
          value[key] = replaceTokens(value[key]);
        });
        return value;
      }
      return value;
    };
    return replaceTokens(payload);
  }

  private extractHistoryRows(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }
    const candidates = [
      response?.responseList,
      response?.data,
      response?.result,
      response?.records,
      response?.rows,
    ];
    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate;
      }
    }
    return [];
  }

  override isFilterActionApplied(action: any): boolean {
    return super.isFilterActionApplied(action);
  }

  override getAppliedFilterSummaryLines(): string[] {
    const lines: string[] = [];
    Object.entries(this.currentFilters || {}).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        lines.push(`${key}: ${value}`);
      }
    });
    return lines.length > 0 ? lines : ['No filters applied'];
  }

  override openFilterDialog(action?: any): void {
    const filterAction = action || this.data?.header?.actions?.find(
      (candidate: any) => candidate?.actionId === 'filter',
    ) || {};
    const attributeRequestEntity = String(filterAction?.attributeRequestEntity || '').trim();
    const attributePayload = filterAction?.attributePayload;

    if (!attributeRequestEntity && !attributePayload) {
      super.openFilterDialog(action);
      return;
    }

    const entityFilterKey = String(this.data?.entityFilterKey || this.data?.entityKey || 'entity');
    const hadEntityFilter = Object.prototype.hasOwnProperty.call(this.currentFilters, entityFilterKey);
    const previousEntity = this.currentFilters[entityFilterKey];
    const previousCustomPayload = this.data?.customPayload;

    if (attributeRequestEntity) {
      this.currentFilters[entityFilterKey] = attributeRequestEntity;
    }
    if (attributePayload) {
      this.data.customPayload = attributePayload;
    }

    try {
      super.openFilterDialog(filterAction);
    } finally {
      if (hadEntityFilter) {
        this.currentFilters[entityFilterKey] = previousEntity;
      } else {
        delete this.currentFilters[entityFilterKey];
      }
      this.data.customPayload = previousCustomPayload;
    }
  }

  constructor(
    configService: ConfigService,
    globalFilterService: GlobalFilterService,
    renderer: Renderer2,
    dialog: MatDialog,
    responsiveLayoutService: ResponsiveLayoutService,
    private customCdr: ChangeDetectorRef,
    http: HttpClient,
    route: ActivatedRoute,
    router: Router,
    private snackBar: MatSnackBar,
    private keycloakService: KeycloakService,
    private elementRef: ElementRef,
    @Optional() @Inject(ENVIRONMENT_CONFIG) environmentConfig: EnvironmentConfig | null,
  ) {
    super(
      configService,
      globalFilterService,
      renderer,
      dialog,
      responsiveLayoutService,
      customCdr,
    );
    this.customGlobalFilterService = globalFilterService;
    this.customConfigService = configService;
    this.http = http;
    this.route = route;
    this.router = router;
    this.environmentConfig = environmentConfig;
  }

  override ngOnInit(): void {
    this.attachEyeLoopDebugListeners();
    this.eyeLoopDebug('ngOnInit', {
      widgetId: this.data?.widgetId,
      route: this.router?.url,
      entity: this.currentFilters?.entity,
    });
    this.applyInitialDropdownValuesFromQueryParams();
    this.captureInitialTableSettings();
    this.restoreSavedTableSettings();
    this.attachFormSchemaListener();
    super.ngOnInit();
    this.loadEntityPermissions(this.resolveSelectedEntityKey());
    this.attachTableRefreshListener();
    this.attachQuoteFormPopupListener();
  }

  override ngOnDestroy(): void {
    this.eyeLoopDebug('ngOnDestroy', {
      widgetId: this.data?.widgetId,
      route: this.router?.url,
    });
    this.detachEyeLoopDebugListeners();
    this.detachFormSchemaListener();
    this.detachTableRefreshListener();
    this.detachQuoteFormPopupListener();
    this.entityPermissionsRequest?.unsubscribe();
    super.ngOnDestroy();
  }

  override openSettingsDialog(): void {
    super.openSettingsDialog();
    this.localDefaults = 'keep';
  }

  override closeSettingsDialog(save: boolean): void {
    if (save) {
      if (this.localDefaults === 'restore') {
        this.restoreInitialTableSettings();
        this.removeSavedTableSettings();
      }

      this.localPageSize = Math.min(1000, Math.max(1, Math.trunc(Number(this.localPageSize) || 50)));
      const columnCount = Array.isArray(this.data?.columns) ? this.data.columns.length : 0;
      this.localFrozenCols = Math.min(columnCount, Math.max(0, Math.trunc(Number(this.localFrozenCols) || 0)));
      this.localDisplayFormat = ['code_name', 'code', 'name'].includes(this.localDisplayFormat)
        ? this.localDisplayFormat
        : 'code_name';
      this.localShowSystemAttribute = this.localShowSystemAttribute === true;

      if (!this.data.pagination) {
        this.data.pagination = {};
      }
      this.data.pagination.pageSize = this.localPageSize;
      this.currentPage = 1;
      this.serverLastPageByEntity.delete(this.serverPaginationEntityKey);
      this.applyPageSizeToPayload(this.data?.customPayload, this.localPageSize);
      this.applyPageSizeToPayload((this as any).pendingDialogRequestData, this.localPageSize);
      this.applyPageSizeToPayload((this as any).appliedDialogFilterPayload, this.localPageSize);

      if (this.localDefaults === 'save') {
        this.saveTableSettings();
      }
    }

    super.closeSettingsDialog(save);
  }

  private captureInitialTableSettings(): void {
    if (this.initialTableSettings) {
      return;
    }

    this.initialTableSettings = {
      pageSize: this.pageSize,
      frozenColumns: this.frozenColumnsCount,
      displayFormat: this.displayFormat,
      showSystemAttribute: this.showSystemAttribute,
    };
  }

  private restoreInitialTableSettings(): void {
    const defaults = this.initialTableSettings;
    if (!defaults) {
      return;
    }

    this.localPageSize = defaults.pageSize;
    this.localFrozenCols = defaults.frozenColumns;
    this.localDisplayFormat = defaults.displayFormat;
    this.localShowSystemAttribute = defaults.showSystemAttribute;
  }

  private restoreSavedTableSettings(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const storedValue = window.localStorage.getItem(this.getTableSettingsStorageKey());
      if (!storedValue) {
        return;
      }
      const settings = JSON.parse(storedValue);
      const pageSize = Math.min(1000, Math.max(1, Math.trunc(Number(settings?.pageSize) || this.pageSize)));
      this.data.pagination = { ...(this.data?.pagination || {}), pageSize };
      this.frozenColumnsCount = Math.max(0, Math.trunc(Number(settings?.frozenColumns) || 0));
      this.displayFormat = ['code_name', 'code', 'name'].includes(settings?.displayFormat)
        ? settings.displayFormat
        : this.displayFormat;
      this.showSystemAttribute = settings?.showSystemAttribute === true;
      this.applyPageSizeToPayload(this.data?.customPayload, pageSize);
    } catch {
      // Ignore unavailable storage or an older malformed preference value.
    }
  }

  private saveTableSettings(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(this.getTableSettingsStorageKey(), JSON.stringify({
        pageSize: this.localPageSize,
        frozenColumns: this.localFrozenCols,
        displayFormat: this.localDisplayFormat,
        showSystemAttribute: this.localShowSystemAttribute,
      }));
    } catch {
      // Settings still apply for this session when persistent storage is unavailable.
    }
  }

  private removeSavedTableSettings(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.removeItem(this.getTableSettingsStorageKey());
    } catch {
      // Nothing else is required when persistent storage is unavailable.
    }
  }

  private getTableSettingsStorageKey(): string {
    const entityKey = this.resolveSelectedEntityKey().trim().toLowerCase() || 'entity';
    return `${EntityManagementComponent.SETTINGS_STORAGE_PREFIX}:${entityKey}`;
  }

  private applyPageSizeToPayload(payload: any, pageSize: number): void {
    const filters = payload?.requestData?.filters;
    if (!Array.isArray(filters)) {
      return;
    }

    const limitFilter = filters.find(
      (filter: any) => String(filter?.attributeName || '').trim().toLowerCase() === 'limit',
    );
    if (limitFilter) {
      limitFilter.attributeValue = pageSize;
    }
    this.updatePayloadOffset(payload, 0);
  }

  override fetchDropdownOptions(dropdown: any): any {
    return super.fetchDropdownOptions(dropdown).pipe(
      map((resolvedDropdown: any) => {
        const additionalOptions = Array.isArray(dropdown?.additionalOptions)
          ? dropdown.additionalOptions
          : [];
        if (additionalOptions.length > 0) {
          const optionValueKey = String(dropdown?.optionValueKey || 'value');
          const mergedOptions = [...(dropdown?.optionsList || [])];
          const existingValues = new Set(
            mergedOptions.map((option: any) => String(option?.[optionValueKey] ?? option)),
          );
          additionalOptions.forEach((option: any) => {
            const value = String(option?.[optionValueKey] ?? option);
            if (!existingValues.has(value)) {
              mergedOptions.push(option);
              existingValues.add(value);
            }
          });
          dropdown.optionsList = mergedOptions;
        }

        if (dropdown?.key === 'entity') {
          dropdown.optionsList = (dropdown?.optionsList || []).map((option: any) => {
            if (!option || typeof option !== 'object') {
              return option;
            }

            const optionValueKey = dropdown?.optionValueKey || 'name';
            const optionLabelKey = dropdown?.optionLabelKey || 'display_name';
            const value = String(option?.[optionValueKey] || '')
              .trim()
              .toLowerCase();
            const label = String(option?.[optionLabelKey] || '')
              .trim()
              .toLowerCase()
              .replace(/^\d+\s*-?\s*/, '');
            const isPaHistoryOption =
              value === RER_AUDIT_ENTITY ||
              ['pa history', 'rer pa history', 'rer audit'].includes(label);
            const displayOption = isPaHistoryOption
              ? { ...option, [optionLabelKey]: 'RER PA History' }
              : option;
            const entityAlias =
              isPaHistoryOption
                ? RER_AUDIT_ENTITY
              : value === 'quote' || label === 'quote'
                ? 'cummins_quote'
                : value === 'quote_details' || value === 'quote_detail' ||
                    label === 'quote details' || label === 'quote detail'
                  ? 'cummins_quote_detail'
                  : value === 'cu_suggested_prices_rer_engine' ||
                      value === 'custom_rer_suggested' ||
                      label === 'rer suggested price'
                    ? 'custom_rer_suggested'
                  : null;

            return entityAlias
              ? { ...displayOption, [optionValueKey]: entityAlias }
              : displayOption;
          });

          // API options are intentionally kept before hardcoded fallbacks. Once
          // aliases such as "150 - Quote" are normalized, remove the fallback
          // with the same entity key so the dropdown shows only one entry.
          const optionValueKey = String(dropdown?.optionValueKey || 'name');
          const hasCustomRerEnginePricing = dropdown.optionsList.some((option: any) =>
            this.normalizeRedirectParamValue(
              option && typeof option === 'object' ? option?.[optionValueKey] : option,
            ).trim().toLowerCase() === 'custom_rer_engine_pricing',
          );
          const seenEntityKeys = new Set<string>();
          dropdown.optionsList = dropdown.optionsList.filter((option: any) => {
            const entityKey = this.normalizeRedirectParamValue(
              option && typeof option === 'object' ? option?.[optionValueKey] : option,
            ).trim().toLowerCase();
            if (hasCustomRerEnginePricing && entityKey === 'cu_rer_engine_pricing') {
              return false;
            }
            if (!entityKey || seenEntityKeys.has(entityKey)) {
              return false;
            }
            seenEntityKeys.add(entityKey);
            return true;
          });

          const previousEntityValue = this.normalizeRedirectParamValue(this.currentFilters?.[dropdown.key]);
          const normalizedEntityKey = this.resolveSelectedEntityKey(dropdown);
          if (normalizedEntityKey) {
            this.currentFilters[dropdown.key] = normalizedEntityKey;
            dropdown.defaultValue = normalizedEntityKey;
            this.syncHeaderTitleFromDropdown(dropdown.key);
            this.syncSelectedEntityLabel(dropdown.key);
            this.applySelectedEntityToPayload(normalizedEntityKey);

            if (previousEntityValue && previousEntityValue !== normalizedEntityKey) {
              this.loadRecords().subscribe();
            }
          }
        }

        if (dropdown?.key === 'model') {
          const normalizedModelKey = this.resolveSelectedDropdownValue(dropdown, this.currentFilters?.[dropdown.key]);
          if (normalizedModelKey) {
            this.currentFilters[dropdown.key] = normalizedModelKey;
            dropdown.defaultValue = normalizedModelKey;
          }
        }

        return resolvedDropdown;
      }),
    );
  }

  override loadRecords(): any {
    const startedAt = performance.now();
    this.requestInitialEntityCount();
    this.prepareRerEnginePricingFetchPayload();
    this.prepareRerAuditFetchPayload();
    this.normalizeDynamicReadPayload();
    this.syncServerPageFromRequestOffset();
    this.eyeLoopDebug('loadRecords:start', {
      widgetId: this.data?.widgetId,
      selectedEntity: this.resolveSelectedEntityKey(),
      filters: this.currentFilters,
      selectedRecordId: this.selectedRecord?.id ?? this.selectedRecord?.uuid ?? null,
    });
    this.applySelectedEntityToPayload();
    this.restoreLastCreatedRecordSnapshot();
    return this.loadTableRecordsForSelectedEntity().pipe(
      tap((records: any[]) => {
        this.trackServerPageBoundary(records);
        // Reapply the most recently created RER row after every table load.
        // This covers manual/widget refreshes where there is no saveContext.
        this.ensureSavedNewRecordIsVisible({
          isNewRecord: true,
          recordSnapshot: this.lastCreatedRecordSnapshot,
        }, false);
        this.eyeLoopDebug('loadRecords:parentResult', {
          widgetId: this.data?.widgetId,
          recordCount: Array.isArray(records) ? records.length : 'not-array',
          selectedEntity: this.resolveSelectedEntityKey(),
        });
      }),
      switchMap((records: any[]) =>
        this.loadHeaderOrder(this.resolveSelectedEntityKey()).pipe(
          map((configuredOrder: string[] | null) => {
            this.columnWidths[0] = EntityManagementComponent.SELECTION_COLUMN_WIDTH;
            this.ensureQuoteApprovalStatusColumn();
            this.ensureRerEnginePricingSuggestedFuturePriceColumn();
            this.filterHiddenColumns();
            this.normalizeColumnLabels();
            if (configuredOrder?.length) {
              this.applyConfiguredColumnOrder(configuredOrder);
            } else {
              this.applyPreferredColumnOrder();
            }
            this.applyKnownColumnOrderFallback();
            this.publishVisibleColumnsSchema();
            this.eyeLoopDebug('loadRecords:afterColumns', {
              widgetId: this.data?.widgetId,
              recordCount: Array.isArray(records) ? records.length : 'not-array',
              columnCount: Array.isArray(this.data?.columns) ? this.data.columns.length : 'not-array',
              selectedEntity: this.resolveSelectedEntityKey(),
            });
            return records;
          }),
        ),
      ),
      finalize(() => this.eyeLoopDebug('loadRecords:finalize', {
        widgetId: this.data?.widgetId,
        elapsedMs: Math.round(performance.now() - startedAt),
        loading: this.loading,
        recordCount: Array.isArray(this.records) ? this.records.length : 'not-array',
      })),
    );
  }

  private requestInitialEntityCount(): void {
    const entityName = this.resolveSelectedEntityKey().trim().toLowerCase();
    const isGomsCustomer = EntityManagementComponent.GOMS_CUSTOMER_ENTITY_KEYS.has(entityName);
    const isRerPaHistory = RER_PA_HISTORY_ENTITIES.has(entityName);
    const supportsCount = isGomsCustomer || RER_PA_HISTORY_ENTITIES.has(entityName) ||
      ['custom_rer_engine_pricing', 'custom_rer_suggested'].includes(entityName);
    if (
      !supportsCount ||
      this.requestedEntityCounts.has(entityName)
    ) {
      return;
    }

    this.requestedEntityCounts.add(entityName);
    this.http.post(this.customConfigService.resolveUrl('/spriced/platform/getData'), {
      entityName: isGomsCustomer ? 's_goms_customer' : entityName,
      attributesList: ['count(*)'],
      filterList: [
        {
          attributeName: isGomsCustomer ? 'id' : 'uuid',
          operator: 'is not',
          logicalOperator: isGomsCustomer ? '' : 'AND',
          beginScope: '',
          endScope: '',
          attributeValue: 'null',
        },
        ...(!isGomsCustomer ? [{
          attributeName: 'is_deleted',
          operator: '=',
          logicalOperator: isRerPaHistory ? 'AND' : '',
          beginScope: '',
          endScope: '',
          attributeValue: false,
        }] : []),
        ...(isRerPaHistory ? [{
          attributeName: 'request_type',
          operator: '=',
          logicalOperator: '',
          beginScope: '',
          endScope: '',
          attributeValue: 'price_change',
        }] : []),
      ],
      requestData: {},
    }).subscribe({
      next: (response) => {
        const count = this.extractEntityRowCount(response, entityName);
        if (count !== null) {
          this.entityRowCounts.set(entityName, count);
        }
      },
      error: (error) => {
        this.requestedEntityCounts.delete(entityName);
        console.error(`Unable to load record count for ${entityName}:`, error);
      },
    });
  }

  get paginationRowCount(): number {
    const entityName = this.resolveSelectedEntityKey().trim().toLowerCase();
    return this.entityRowCounts.get(entityName) ?? this.records.length;
  }

  private extractEntityRowCount(response: any, entityName: string): number | null {
    if (typeof response === 'number' || typeof response === 'string') {
      const count = typeof response === 'string' && !response.trim() ? NaN : Number(response);
      return Number.isSafeInteger(count) && count >= 0 ? count : null;
    }
    if (!response || typeof response !== 'object') {
      return null;
    }
    if (Array.isArray(response)) {
      return this.extractEntityRowCount(response[0], entityName);
    }
    for (const key of ['count(*)', 'count', entityName, 'data', 'responseList']) {
      const count = this.extractEntityRowCount(response[key], entityName);
      if (count !== null) {
        return count;
      }
    }
    return null;
  }

  override get totalPages(): number {
    if (!this.isServerPaginatedEntity) {
      return super.totalPages;
    }

    const knownLastPage = this.serverLastPageByEntity.get(this.serverPaginationEntityKey);
    if (knownLastPage !== undefined) {
      return Math.max(this.currentPage, knownLastPage);
    }

    const hasNextPage = (this.records?.length || 0) >= this.pageSize;
    return this.currentPage + (hasNextPage ? 1 : 0);
  }

  override get pagedRecords(): any[] {
    return this.isServerPaginatedEntity ? (this.records || []) : super.pagedRecords;
  }

  override goToPage(page: number): void {
    if (!this.isServerPaginatedEntity) {
      super.goToPage(page);
      return;
    }

    const targetPage = Math.max(1, Math.trunc(Number(page) || 1));
    if (this.loading || targetPage === this.currentPage || targetPage > this.totalPages) {
      return;
    }

    const previousPage = this.currentPage;
    const previousRecords = this.records;
    const previousSelectedRecord = this.selectedRecord;
    const offset = (targetPage - 1) * this.pageSize;

    this.setServerRequestOffset(offset);
    this.currentPage = targetPage;
    this.loadRecords().subscribe((records: any[]) => {
      // When the final populated page contains exactly 50 rows, the API cannot
      // advertise that there is no following page. Keep the user on the last
      // populated page if the probe for the next batch is empty.
      if (targetPage > previousPage && records.length === 0 && previousRecords.length > 0) {
        this.serverLastPageByEntity.set(this.serverPaginationEntityKey, previousPage);
        this.currentPage = previousPage;
        this.records = previousRecords;
        this.setServerRequestOffset(
          (previousPage - 1) * this.pageSize,
        );
        this.selectRecord(previousSelectedRecord || previousRecords[0]);
      }
    });
  }

  override goToPreviousPage(): void {
    if (this.isServerPaginatedEntity) {
      this.goToPage(Math.max(1, this.currentPage - 1));
      return;
    }
    super.goToPreviousPage();
  }

  override goToNextPage(): void {
    if (this.isServerPaginatedEntity) {
      this.goToPage(this.currentPage + 1);
      return;
    }
    super.goToNextPage();
  }

  protected override resetPagination(): void {
    if (!this.isServerPaginatedEntity) {
      super.resetPagination();
    }
  }

  private get isServerPaginatedEntity(): boolean {
    return EntityManagementComponent.SERVER_PAGINATED_ENTITY_KEYS.has(this.serverPaginationEntityKey);
  }

  private get serverPaginationEntityKey(): string {
    return this.resolveSelectedEntityKey().trim().toLowerCase();
  }

  private trackServerPageBoundary(records: any[]): void {
    if (!this.isServerPaginatedEntity || !Array.isArray(records)) {
      return;
    }

    if (records.length < this.pageSize) {
      this.serverLastPageByEntity.set(this.serverPaginationEntityKey, this.currentPage);
    } else if (this.serverLastPageByEntity.get(this.serverPaginationEntityKey) === this.currentPage) {
      this.serverLastPageByEntity.delete(this.serverPaginationEntityKey);
    }
  }

  private syncServerPageFromRequestOffset(): void {
    if (!this.isServerPaginatedEntity) {
      return;
    }

    const pendingPayload = (this as any).pendingDialogRequestData;
    const payload = pendingPayload || this.data?.customPayload;
    const offset = this.getServerRequestOffset(payload);
    this.currentPage = Math.floor(offset / this.pageSize) + 1;
  }

  private getServerRequestOffset(payload: any): number {
    const filters = payload?.requestData?.filters || payload?.filterList;
    const offsetFilter = Array.isArray(filters)
      ? filters.find((filter: any) => String(filter?.attributeName || '').trim().toLowerCase() === 'offset')
      : null;
    const offset = Number(offsetFilter?.attributeValue);
    return Number.isFinite(offset) && offset > 0 ? offset : 0;
  }

  private setServerRequestOffset(offset: number): void {
    const normalizedOffset = Math.max(0, Math.trunc(offset));
    this.updatePayloadOffset(this.data?.customPayload, normalizedOffset);

    const appliedPayload = (this as any).appliedDialogFilterPayload;
    if (this.hasDialogFilterApplied && appliedPayload) {
      this.updatePayloadOffset(appliedPayload, normalizedOffset);
      (this as any).pendingDialogRequestData = this.cloneCellActionPayload(appliedPayload);
    } else if (this.serverPaginationEntityKey === 'custom_rer_engine_pricing') {
      (this as any).pendingDialogRequestData = this.buildRerEnginePricingFetchPayload(normalizedOffset);
    } else if (this.serverPaginationEntityKey === EntityManagementComponent.RER_AUDIT_ENTITY) {
      (this as any).pendingDialogRequestData = this.buildRerAuditFetchPayload(normalizedOffset);
    }
  }

  private updatePayloadOffset(payload: any, offset: number): void {
    const filters = payload?.requestData?.filters || payload?.filterList;
    if (!Array.isArray(filters)) {
      return;
    }

    const offsetFilter = filters.find(
      (filter: any) => String(filter?.attributeName || '').trim().toLowerCase() === 'offset',
    );
    if (offsetFilter) {
      offsetFilter.attributeValue = offset;
    }
  }

  private normalizeDynamicReadPayload(): void {
    const pendingPayload = (this as any).pendingDialogRequestData;
    if (!this.isQuoteIntegrationFilterApplyPayload(pendingPayload)) {
      return;
    }

    this.removeDefaultUuidFilter(pendingPayload);
    // The parent keeps a clone for table refreshes after a filter has been
    // applied. Keep that copy aligned with the payload sent on the first apply.
    this.removeDefaultUuidFilter((this as any).appliedDialogFilterPayload);
  }

  private isQuoteIntegrationFilterApplyPayload(payload: any): boolean {
    const entity = String(payload?.requestData?.entity || '').trim().toLowerCase();
    const isFilterDialogApply = Object.prototype.hasOwnProperty.call(
      payload || {},
      '__appliedFilterSummaryLines',
    );

    return isFilterDialogApply && EntityManagementComponent.QUOTE_INTEGRATION_ENTITY_KEYS.has(entity);
  }

  private removeDefaultUuidFilter(payload: any): void {
    const filters = payload?.requestData?.filters;
    if (Array.isArray(filters)) {
      payload.requestData.filters = filters.filter((filter: any) => !this.isDefaultUuidFilter(filter));
    }
  }

  private isDefaultUuidFilter(filter: any): boolean {
    return String(filter?.attributeName || '').trim().toLowerCase() === 'uuid'
      && String(filter?.operator || '').trim().toLowerCase() === 'is_not_null'
      && String(filter?.attributeValue ?? '').trim().toLowerCase() === 'null';
  }

  isApprovalStatusColumn(column: any): boolean {
    const entity = this.normalizeOrderToken(this.resolveSelectedEntityKey());
    if (entity !== 'cummins quote' && entity !== 'quote') {
      return false;
    }

    const key = this.normalizeOrderToken(column?.key || '');
    const label = this.normalizeOrderToken(column?.label || this.getColumnDisplayLabel(column));
    return ['approval status', 'approvals status'].includes(key) ||
      ['approval status', 'approvals status'].includes(label);
  }

  private ensureQuoteApprovalStatusColumn(): void {
    const entity = this.normalizeOrderToken(this.resolveSelectedEntityKey());
    if (entity !== 'cummins quote' && entity !== 'quote') {
      return;
    }

    // The parent creates columns after records arrive. Avoid creating a lone
    // approval column for an empty result, which would prevent later inference.
    if (!Array.isArray(this.data?.columns)) {
      return;
    }

    if (this.data.columns.some((column: any) => this.isApprovalStatusColumn(column))) {
      return;
    }

    this.data.columns = [
      ...this.data.columns,
      {
        key: 'approvals_status',
        label: 'Approval Status',
        sortable: false,
      },
    ];
    this.columnWidths.push(150);
  }

  private ensureRerEnginePricingSuggestedFuturePriceColumn(): void {
    const entity = this.resolveSelectedEntityKey().trim().toLowerCase();
    if (!EntityManagementComponent.RER_ENGINE_PRICING_ENTITY_KEYS.has(entity) || !Array.isArray(this.data?.columns)) {
      return;
    }

    const fieldKey = 'suggested_future_price';
    const hasColumn = this.data.columns.some((column: any) =>
      this.normalizeSchemaKey(column?.key) === fieldKey,
    );
    if (hasColumn) {
      return;
    }

    this.data.columns = [
      ...this.data.columns,
      {
        key: fieldKey,
        label: 'Suggested Future Price',
        sortable: true,
      },
    ];
    this.columnWidths.push(150);
  }

  openApprovalStatusDialog(event: MouseEvent, record: any, column: any): void {
    event.stopPropagation();
    this.approvalRows = [];
    this.approvalLoadError = '';
    this.approvalLoading = false;

    if (this.approvalStatusDialog && this.dialog) {
      this.approvalDialogRef = this.dialog.open(this.approvalStatusDialog, {
        width: '83vw',
        height: '82vh',
        maxWidth: 'none',
        maxHeight: '86vh',
        panelClass: 'approval-status-dialog-panel',
        autoFocus: false,
      });
    }

    const embeddedApproval = this.findEmbeddedApprovalData(record, column);
    if (embeddedApproval) {
      this.approvalRows = this.mapApprovalRows(embeddedApproval);
    }
  }

  closeApprovalStatusDialog(): void {
    this.approvalDialogRef?.close();
    this.approvalDialogRef = undefined;
  }

  updateApprovalStatus(row: { status: string }, status: 'APPROVED' | 'REJECTED'): void {
    row.status = status;
  }

  isPendingApproval(status: string): boolean {
    return String(status || '').trim().toUpperCase() === 'PENDING';
  }

  approvalStatusClass(status: string): string {
    return `approval-status-${String(status || 'unknown').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  }

  onApprovalColumnDragStart(event: DragEvent, columnKey: string): void {
    this.draggedApprovalColumnKey = columnKey;
    event.dataTransfer?.setData('text/plain', columnKey);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  onApprovalColumnDragOver(event: DragEvent, columnKey: string): void {
    event.preventDefault();
    if (this.draggedApprovalColumnKey && this.draggedApprovalColumnKey !== columnKey) {
      this.approvalDropTargetKey = columnKey;
    }
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  onApprovalColumnDrop(event: DragEvent, targetColumnKey: string): void {
    event.preventDefault();
    const sourceColumnKey = this.draggedApprovalColumnKey || event.dataTransfer?.getData('text/plain');
    const sourceIndex = this.approvalColumns.findIndex((column) => column.key === sourceColumnKey);
    const targetIndex = this.approvalColumns.findIndex((column) => column.key === targetColumnKey);

    if (sourceIndex >= 0 && targetIndex >= 0 && sourceIndex !== targetIndex) {
      const reorderedColumns = [...this.approvalColumns];
      const [movedColumn] = reorderedColumns.splice(sourceIndex, 1);
      reorderedColumns.splice(targetIndex, 0, movedColumn);
      this.approvalColumns = reorderedColumns;
    }

    this.onApprovalColumnDragEnd();
  }

  onApprovalColumnDragEnd(): void {
    this.draggedApprovalColumnKey = null;
    this.approvalDropTargetKey = null;
  }

  override openCellActionDialog(event: MouseEvent, record: any, column: any, action: any): void {
    event.preventDefault();
    event.stopPropagation();
    (event as any).stopImmediatePropagation?.();
    this.eyeLoopDebug('eyeClick:start', {
      eventType: event.type,
      widgetId: this.data?.widgetId,
      actionId: action?.actionId,
      columnKey: column?.key,
      columnLabel: column?.label || this.getColumnDisplayLabel(column),
      selectedEntity: this.resolveSelectedEntityKey(),
      recordId: record?.id ?? record?.uuid ?? record?.quote_id ?? record?.quoteId ?? null,
      recordKeys: record && typeof record === 'object' ? Object.keys(record).slice(0, 30) : [],
    });

    if (!this.cellActionDialog || !this.dialog) {
      this.eyeLoopDebug('eyeClick:missingDialogRefs', {
        hasTemplate: !!this.cellActionDialog,
        hasDialog: !!this.dialog,
      });
      return;
    }

    try {
      const rawCellValue = this.getRecordColumnValue(record, column);
      const cellValue = this.parseRowJsonValue(rawCellValue);
      const parsedDialogRows = cellValue && typeof cellValue === 'object'
        ? this.mapCellJsonRows(cellValue, action)
        : this.buildCustomCellDialogRows(action, record, column, cellValue);
      const dialogRows = this.enrichCellDialogRows(parsedDialogRows, record, action);

      const cellDisplayValue = this.formatCellValue(rawCellValue, column);
      const titleValue = this.pickCustomValueByCandidateKeys(record, action?.titleValueKeys);
      const fallbackId =
        titleValue ??
        record?.quote_id ??
        record?.quoteId ??
        record?.id ??
        record?.uuid ??
        cellDisplayValue ??
        '';

      this.activeCellDialogConfig = action;
      this.activeCellDialogRecord = record;
      this.activeCellDialogTitle = this.interpolateCustomCellActionTitle(
        action?.dialogTitle || action?.title || 'Edit Transactions',
        { record, column, value: cellDisplayValue, fallbackId },
      );
      const configuredDialogColumns = this.normalizeCustomCellDialogColumns(action);
      this.activeCellDialogColumns = configuredDialogColumns.length
        ? configuredDialogColumns
        : action?.autoColumnsFromJson === true
          ? this.buildCustomCellDialogColumnsFromRows(dialogRows)
          : [];
      this.activeCellDialogRows = this.normalizeCustomCellDialogRows(dialogRows, this.activeCellDialogColumns);
      this.activeCellDialogPageSize = action?.pageSize || this.activeCellDialogRows.length || 10;
      this.refreshCellDialogEmptyRows();
      this.activeCellDialogAnnotation = action?.annotations?.defaultValue || '';
      this.cellDialogSaveError = '';

      this.eyeLoopDebug('eyeClick:beforeDialogOpen', {
        rawCellValueType: Array.isArray(rawCellValue) ? 'array' : typeof rawCellValue,
        parsedCellValueType: Array.isArray(cellValue) ? 'array' : typeof cellValue,
        rowCount: this.activeCellDialogRows.length,
        columnCount: this.activeCellDialogColumns.length,
        title: this.activeCellDialogTitle,
        firstRowKeys: Object.keys(this.activeCellDialogRows[0] || {}),
        firstRow: this.serializeEyeLoopPayload(this.activeCellDialogRows[0] || null),
        columns: this.serializeEyeLoopPayload(this.activeCellDialogColumns),
      });

      this.cellActionDialogRef = this.dialog.open(this.cellActionDialog, {
        width: action?.dialogWidth || '78vw',
        maxWidth: action?.dialogMaxWidth || '90vw',
        height: action?.dialogHeight,
        maxHeight: action?.dialogMaxHeight || '90vh',
        panelClass: 'entity-cell-action-dialog-panel',
      });

      this.eyeLoopDebug('eyeClick:afterDialogOpen', {
        hasDialogRef: !!this.cellActionDialogRef,
      });
    } catch (error) {
      this.eyeLoopDebug('eyeClick:thrown', this.serializeEyeLoopError(error));
      throw error;
    }
  }

  override addCellDialogRow(): void {
    if (!Array.isArray(this.activeCellDialogRows)) {
      this.activeCellDialogRows = [];
    }

    this.activeCellDialogRows.push({
      ...this.buildCustomEmptyCellDialogRow(),
      __isNew: true,
      __isDirty: true,
    });
    this.refreshCellDialogEmptyRows();
  }

  markCellDialogRowDirty(row: any): void {
    if (row && typeof row === 'object') {
      row.__isDirty = true;
    }
  }

  onCellDialogSelectChange(row: any, columnKey: string, value: any): void {
    if (!row || typeof row !== 'object' || !columnKey) {
      return;
    }

    row[columnKey] = value;
    this.markCellDialogRowDirty(row);
  }

  override saveCellActionDialog(): void {
    if (this.cellDialogSaving) {
      return;
    }

    if (this.activeCellDialogConfig?.saveLocally === true) {
      this.saveQuoteBillToShipToRowsLocally();
      return;
    }

    const dirtyRows = (Array.isArray(this.activeCellDialogRows) ? this.activeCellDialogRows : [])
      .filter((row: any) => row?.__isDirty);
    this.applyCellDialogSaveDerivedFields(dirtyRows);

    const rowsToSave = dirtyRows
      .filter((row: any) => this.hasCellDialogRequestValues(row));

    if (!rowsToSave.length) {
      this.closeCellActionDialog();
      this.refreshQuoteTableAfterCellDialogSave().subscribe();
      return;
    }

    this.cellDialogSaving = true;
    this.cellDialogSaveError = '';
    const saveApiUrl = this.activeCellDialogConfig?.saveApiUrl ||
      this.activeCellDialogConfig?.sendApiUrl ||
      '/spriced/platform';
    const resolvedSaveApiUrl = this.customConfigService.resolveUrl(saveApiUrl);

    from(rowsToSave).pipe(
      concatMap((row: any) => this.http.post(
        resolvedSaveApiUrl,
        this.buildCellDialogSavePayload(row),
        { headers: this.buildBillToShipToLookupHeaders() },
      )),
      toArray(),
      tap(() => this.closeCellActionDialog()),
      switchMap(() => this.refreshQuoteTableAfterCellDialogSave()),
      finalize(() => {
        this.cellDialogSaving = false;
      }),
    ).subscribe({
      error: (error: any) => {
        this.cellDialogSaveError = error?.error?.message || error?.message || 'Unable to save the transaction.';
        this.eyeLoopDebug('cellDialogSave:error', this.serializeEyeLoopError(error));
      },
    });
  }

  getCellDialogColumnOptions(row: any, column: any): any[] {
    const rowOptions = row?.[`${column?.key}_options`];

    if (column?.key === 'ship_to' && Array.isArray(rowOptions) && rowOptions.length) {
      return rowOptions;
    }

    if (Array.isArray(column?.options) && column.options.length) {
      return column.options;
    }

    return Array.isArray(rowOptions) ? rowOptions : [];
  }

  override removeCellDialogRow(index: number): void {
    super.removeCellDialogRow(index);
    this.refreshCellDialogEmptyRows();
  }

  override getCellDialogEmptyRows(): number[] {
    return this.activeCellDialogEmptyRows;
  }

  trackByCellDialogColumn(_index: number, column: any): string {
    return String(column?.key || column?.label || _index);
  }

  trackByCellDialogRow(index: number, row: any): string {
    return String(row?.__dialogRowId || row?.uuid || row?.id || index);
  }

  trackByCellDialogOption(_index: number, option: any): string {
    return String(option?.value ?? option?.label ?? _index);
  }

  trackByCellDialogEmptyRow(_index: number, rowNumber: number): number {
    return rowNumber;
  }

  override sendCellActionDialog(row?: any, rowIndex?: number): void {
    this.applyCellDialogSendDerivedFields(row);

    if (!row) {
      this.publishCellActionSend(row, rowIndex);
      return;
    }

    const billToValue = String(row?.bill_to ?? '').trim();
    if (!billToValue) {
      return;
    }

    row.__shipToLoading = true;
    this.http.post(
      this.customConfigService.resolveUrl(
        this.activeCellDialogConfig?.sendApiUrl || '/spriced/platform',
      ),
      this.buildBillToShipToLookupPayload(billToValue),
      { headers: this.buildBillToShipToLookupHeaders() },
    )
      .pipe(catchError(() => of(null)))
      .subscribe((response: any) => {
        const options = this.extractShipToOptions(response);
        row.ship_to_options = options;
        row.ship_to_control_type = 'select';
        row.__shipToLoading = false;
        if (options.length === 1) {
          row.ship_to = options[0].value;
        } else if (row.ship_to && !options.some((option) => option.value === row.ship_to)) {
          row.ship_to = '';
        }
        this.publishCellActionSend(row, rowIndex, response);
      });
  }

  private applyCellDialogSendDerivedFields(row?: any): void {
    const rules = Array.isArray(this.activeCellDialogConfig?.sendDerivedFields)
      ? this.activeCellDialogConfig.sendDerivedFields
      : [];
    this.applyCellDialogDerivedFields(rules, row);
  }

  private applyCellDialogSaveDerivedFields(rows: any[]): void {
    const rules = Array.isArray(this.activeCellDialogConfig?.saveDerivedFields)
      ? this.activeCellDialogConfig.saveDerivedFields
      : [];
    this.applyCellDialogDerivedFields(rules, rows, false);
  }

  private applyCellDialogDerivedFields(rules: any[], rowOrRows?: any, markDirty = true): void {
    if (!rules.length) {
      return;
    }

    const rows = Array.isArray(rowOrRows)
      ? rowOrRows
      : rowOrRows
        ? [rowOrRows]
        : Array.isArray(this.activeCellDialogRows)
          ? this.activeCellDialogRows
          : [];

    rows.forEach((dialogRow: any) => {
      if (!dialogRow || typeof dialogRow !== 'object') {
        return;
      }

      rules.forEach((rule: any) => {
        const targetKey = String(rule?.targetKey || '').trim();
        const sourceKey = String(rule?.sourceKey || '').trim();
        if (!targetKey || !sourceKey) {
          return;
        }

        const sourceValue = this.getCellDialogRowValue(dialogRow, sourceKey);
        const derivedValue = String(sourceValue ?? '').trim()
          ? rule?.filledValue
          : rule?.emptyValue;
        this.setCellDialogRowValue(dialogRow, targetKey, derivedValue ?? '');
        if (markDirty) {
          this.markCellDialogRowDirty(dialogRow);
        }
      });
    });
  }

  private getCellDialogRowValue(row: any, key: string): any {
    if (!row || typeof row !== 'object') {
      return undefined;
    }

    if (Object.prototype.hasOwnProperty.call(row, key)) {
      return row[key];
    }

    const normalizedKey = this.normalizeOrderToken(key);
    const matchingKey = Object.keys(row).find((rowKey) => this.normalizeOrderToken(rowKey) === normalizedKey);
    return matchingKey ? row[matchingKey] : undefined;
  }

  private setCellDialogRowValue(row: any, key: string, value: any): void {
    if (!row || typeof row !== 'object') {
      return;
    }

    row[key] = value;

    const normalizedKey = this.normalizeOrderToken(key);
    const matchingKey = Object.keys(row).find((rowKey) =>
      rowKey !== key && this.normalizeOrderToken(rowKey) === normalizedKey,
    );
    if (matchingKey) {
      row[matchingKey] = value;
    }
  }

  override selectRecord(record: any): void {
    this.eyeLoopDebug('selectRecord', {
      widgetId: this.data?.widgetId,
      recordId: record?.id ?? record?.uuid ?? record?.quote_id ?? record?.quoteId ?? null,
      recordKeys: record && typeof record === 'object' ? Object.keys(record).slice(0, 30) : [],
      selectedEntity: this.resolveSelectedEntityKey(),
      stack: this.getCompactEyeLoopStack(),
    });
    super.selectRecord(record);
  }

  private publishCellActionSend(row?: any, rowIndex?: number, response?: any): void {
    if (!this.data?.widgetId) {
      return;
    }

    const actionId = this.activeCellDialogConfig?.sendActionId ||
      `${this.activeCellDialogConfig?.actionId || 'cell_action'}_send`;
    const payload: any = {
      rows: this.activeCellDialogRows,
      annotation: this.activeCellDialogAnnotation,
    };
    if (row) {
      payload.row = row;
      payload.rowIndex = rowIndex;
    }
    if (response !== undefined) {
      payload.response = response;
    }

    this.customGlobalFilterService.updateFilterSelection(
      `${this.data.widgetId}_${actionId}`,
      [payload],
      false,
      this.data.widgetId,
    );
  }

  private buildBillToShipToLookupPayload(billToValue: string): any {
    return {
      requestData: {
        entity: 'cummins_bill_to_ship_to_config',
        filters: [
          {
            attributeName: 'billtocode',
            operator: '=',
            attributeValue: billToValue,
            attributeType: 'string',
            logicalOperator: '',
            beginScope: '',
            endScope: '',
          },
          {
            attributeName: 'limit',
            operator: 'is',
            logicalOperator: '',
            beginScope: '',
            endScope: '',
            attributeValue: 50,
          },
          {
            attributeName: 'offset',
            operator: 'is',
            logicalOperator: '',
            beginScope: '',
            endScope: '',
            attributeValue: 0,
          },
          {
            attributeName: 'order by',
            operator: 'is',
            logicalOperator: '',
            beginScope: '',
            endScope: '',
            attributeValue: 'uuid desc',
          },
        ],
      },
      responseList: [],
      operation: null,
      entity: 'product',
      batchId: null,
      workflowId: 'UI',
      endpoints: [
        {
          functionName: 'NestedDynamicReadServices',
          version: '1.0',
        },
      ],
      version: '1.0',
      businessId: 'D1CCD304BF414093954DE31FBD49CFC7-product-1780576983958',
      userId: null,
      error: null,
      commitStatus: 'NO_COMMIT',
      commitOffset: 12367008,
    };
  }

  private buildBillToShipToLookupHeaders(): Record<string, string> {
    const token = this.keycloakService.getKeycloakInstance()?.token;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  private buildCellDialogSavePayload(row: any): any {
    const functionName = this.activeCellDialogConfig?.saveFunctionName || 'CustomQuoteSaveService';
    const code = this.pickCustomValueByCandidateKeys(
      this.activeCellDialogRecord,
      this.activeCellDialogConfig?.saveCodeKeys || ['code', 'Code', 'quote_code', 'Quote Code'],
    );
    const configuredRequestData = this.buildConfiguredCellDialogSaveRequestData(row, code);

    if (configuredRequestData) {
      return {
        requestData: configuredRequestData,
        responseList: [],
        entity: this.activeCellDialogConfig?.saveEntity || 'product',
        workflowId: this.activeCellDialogConfig?.saveWorkflowId || 'UI',
        endpoints: [
          {
            functionName,
            version: this.activeCellDialogConfig?.saveVersion || '1.0',
          },
        ],
        communicationType: this.activeCellDialogConfig?.saveCommunicationType || 'rest',
        version: this.activeCellDialogConfig?.saveVersion || '1.0',
        functionName,
      };
    }

    return {
      requestData: {
        code: String(code ?? '').trim(),
        bill_to_code: String(row?.bill_to ?? '').trim(),
        ship_to_code: String(row?.ship_to ?? '').trim(),
        override_date: this.formatCellDialogRequestDate(row?.override),
        active: String(row?.active ?? '').trim().toLowerCase(),
        operation: this.activeCellDialogConfig?.saveOperation || 'insert',
      },
      responseList: [],
      entity: 'product',
      workflowId: 'UI',
      endpoints: [
        {
          functionName,
          version: '1.0',
        },
      ],
      communicationType: 'rest',
      version: '1.0',
      functionName,
    };
  }

  private formatCellDialogRequestDate(value: any): string {
    const normalizedDate = this.formatCellJsonDate(value);
    return normalizedDate ? `${normalizedDate}T00:00:00` : '';
  }

  private hasCellDialogRequestValues(row: any): boolean {
    const configuredKeys = this.getConfiguredCellDialogSaveValueKeys();
    if (configuredKeys.length) {
      return configuredKeys.some((key) => String(row?.[key] ?? '').trim() !== '');
    }

    return ['bill_to', 'ship_to', 'active', 'override']
      .some((key) => String(row?.[key] ?? '').trim() !== '');
  }

  private buildConfiguredCellDialogSaveRequestData(row: any, code: any): Record<string, any> | null {
    const fields = Array.isArray(this.activeCellDialogConfig?.saveRequestFields)
      ? this.activeCellDialogConfig.saveRequestFields
      : [];

    if (!fields.length) {
      return null;
    }

    const requestData: Record<string, any> = {
      code: String(code ?? '').trim(),
      operation: this.activeCellDialogConfig?.saveOperation || 'insert',
    };

    fields.forEach((field: any) => {
      const targetKey = String(field?.targetKey ?? field?.key ?? '').trim();
      const sourceKey = String(field?.sourceKey ?? field?.field ?? field?.key ?? targetKey).trim();
      if (!targetKey) {
        return;
      }

      const value = field?.value !== undefined
        ? field.value
        : row?.[sourceKey];
      requestData[targetKey] = field?.type === 'date'
        ? this.formatCellDialogRequestDate(value)
        : String(value ?? '').trim();
    });

    return requestData;
  }

  private getConfiguredCellDialogSaveValueKeys(): string[] {
    const configuredKeys = Array.isArray(this.activeCellDialogConfig?.saveValueKeys)
      ? this.activeCellDialogConfig.saveValueKeys
      : [];

    if (configuredKeys.length) {
      return configuredKeys.map((key: any) => String(key || '').trim()).filter(Boolean);
    }

    const saveFields = Array.isArray(this.activeCellDialogConfig?.saveRequestFields)
      ? this.activeCellDialogConfig.saveRequestFields
      : [];

    return saveFields
      .map((field: any) => String(field?.sourceKey ?? field?.field ?? field?.key ?? '').trim())
      .filter(Boolean);
  }

  private refreshQuoteTableAfterCellDialogSave(): any {
    (this as any).pendingDialogRequestData = this.buildCellDialogRefreshPayload();
    return this.loadRecords();
  }

  private buildCellDialogRefreshPayload(): any {
    const configuredPayload = this.activeCellDialogConfig?.refreshPayload;
    if (configuredPayload && typeof configuredPayload === 'object') {
      return this.cloneCellActionPayload(configuredPayload);
    }

    const selectedEntity = this.resolveSelectedEntityKey() || 'cummins_quote';

    return {
      requestData: {
        entity: selectedEntity,
        filters: [
          {
            attributeName: 'uuid',
            operator: 'is_not_null',
            attributeValue: 'null',
            attributeType: 'string',
            logicalOperator: '',
            beginScope: '',
            endScope: '',
          },
          {
            attributeName: 'limit',
            operator: 'is',
            logicalOperator: '',
            beginScope: '',
            endScope: '',
            attributeValue: 50,
          },
          {
            attributeName: 'offset',
            operator: 'is',
            logicalOperator: '',
            beginScope: '',
            endScope: '',
            attributeValue: 0,
          },
          {
            attributeName: 'order by',
            operator: 'is',
            logicalOperator: '',
            beginScope: '',
            endScope: '',
            attributeValue: 'uuid desc',
          },
        ],
      },
      responseList: [],
      entity: 'product',
      workflowId: 'UI',
      endpoints: [
        {
          functionName: 'NestedDynamicReadServices',
          version: '1.0',
        },
      ],
      communicationType: 'rest',
      version: '1.0',
    };
  }

  private extractShipToOptions(response: any): Array<{ label: string; value: string }> {
    const values = new Set<string>();
    const parsedResponse = this.parseRowJsonValue(response);
    const addValue = (value: any) => {
      if (value !== null && value !== undefined && String(value).trim()) {
        values.add(String(value).trim());
      }
    };

    const configResponses = Array.isArray(parsedResponse?.responseList)
      ? parsedResponse.responseList
      : [];
    configResponses.forEach((responseEntry: any) => {
      const configs = Array.isArray(responseEntry?.cummins_bill_to_ship_to_config)
        ? responseEntry.cummins_bill_to_ship_to_config
        : [];
      configs.forEach((config: any) => {
        const dataRows = Array.isArray(config?.data) ? config.data : [];
        dataRows.forEach((dataRow: any) => {
          const shipToRows = Array.isArray(dataRow?.shiptocode_customername)
            ? dataRow.shiptocode_customername
            : [];
          shipToRows.forEach((shipTo: any) => addValue(shipTo?.ship_to_code));
        });
      });
    });

    const visit = (candidate: any) => {
      const parsedCandidate = this.parseRowJsonValue(candidate);
      if (Array.isArray(parsedCandidate)) {
        parsedCandidate.forEach(visit);
        return;
      }
      if (!parsedCandidate || typeof parsedCandidate !== 'object') {
        return;
      }

      const directValue =
        parsedCandidate.ship_to_code ??
        parsedCandidate.sap_ship_to_code ??
        parsedCandidate.ShipToCode ??
        parsedCandidate['Ship To Code'] ??
        parsedCandidate.shipToCode;
      addValue(directValue);

      Object.values(parsedCandidate).forEach(visit);
    };

    visit(parsedResponse);
    return Array.from(values).map((value) => ({ label: value, value }));
  }

  private buildCustomEmptyCellDialogRow(): any {
    const fields = Array.isArray(this.activeCellDialogConfig?.detailFields)
      ? this.activeCellDialogConfig.detailFields
      : [];
    const fieldsByKey = fields.reduce((acc: any, field: any) => {
      if (field?.key) {
        acc[field.key] = field;
      }
      return acc;
    }, {});

    return (Array.isArray(this.activeCellDialogColumns) ? this.activeCellDialogColumns : [])
      .reduce((row: any, column: any) => {
        const key = column?.key;
        if (!key || column?.type === 'button') {
          return row;
        }

        const field = fieldsByKey[key] || column;
        row[key] = '';
        row[`${key}_control_type`] = column?.controlType || field?.controlType || 'text';
        row[`${key}_options`] = this.resolveCustomCellDialogOptions(field);
        return row;
      }, {});
  }

  private resolveCustomCellDialogOptions(field: any): any[] {
    const configuredOptions = Array.isArray(field?.options) ? field.options : [];
    return configuredOptions.map((option: any) => {
      if (option && typeof option === 'object') {
        return {
          label: option.label ?? option.display_name ?? option.displayName ?? option.name ?? option.value ?? '',
          value: option.value ?? option.name ?? option.label ?? option.display_name ?? option.displayName ?? '',
        };
      }

      return { label: String(option), value: option };
    });
  }

  private findEmbeddedApprovalData(record: any, column: any): any | null {
    if (!record || typeof record !== 'object') {
      return null;
    }

    const candidates = [
      this.getRecordColumnValue(record, column),
      record.approval,
      record.approvalStatus,
      record.approval_status,
      record.approvalsStatus,
      record.approvals_status,
      record.approvalData,
      record.approval_data,
    ].map((candidate) => this.parseRowJsonValue(candidate));
    return candidates.find((candidate) => candidate && typeof candidate === 'object') || null;
  }

  private getRecordColumnValue(record: any, column: any): any {
    if (!record || typeof record !== 'object') {
      return null;
    }

    if (column?.key && Object.prototype.hasOwnProperty.call(record, column.key)) {
      return record[column.key];
    }

    const targetTokens = [column?.key, column?.label]
      .map((value) => this.normalizeOrderToken(value || ''))
      .filter(Boolean);
    const matchingEntry = Object.entries(record).find(([key]) =>
      targetTokens.includes(this.normalizeOrderToken(key)),
    );
    return matchingEntry?.[1] ?? null;
  }

  private parseRowJsonValue(value: any): any {
    let parsedValue = value;
    for (let attempt = 0; attempt < 2 && typeof parsedValue === 'string'; attempt += 1) {
      const textValue = parsedValue.trim();
      if (!textValue || (!textValue.startsWith('{') && !textValue.startsWith('[') && !textValue.startsWith('"'))) {
        break;
      }
      try {
        parsedValue = JSON.parse(textValue);
      } catch {
        break;
      }
    }

    return this.parseLegacyEmbeddedJsonRows(parsedValue);
  }

  /**
   * Some Quote SAP Config responses use a legacy set-like representation:
   * {"{\"plant\":...}","{\"plant\":...}"}. It is not valid JSON as a
   * whole, but each quoted entry is a valid encoded JSON object.
   */
  private parseLegacyEmbeddedJsonRows(value: any): any {
    if (typeof value !== 'string') {
      return value;
    }

    const textValue = value.trim();
    if (!textValue.startsWith('{') || !textValue.includes('\\"')) {
      return value;
    }

    const encodedEntries = textValue.match(/"(?:\\.|[^"\\])*"/g) || [];
    const rows = encodedEntries.reduce((parsedRows: any[], encodedEntry: string) => {
      try {
        const decodedEntry = JSON.parse(encodedEntry);
        if (typeof decodedEntry !== 'string') {
          return parsedRows;
        }

        const row = JSON.parse(decodedEntry);
        if (row && typeof row === 'object' && !Array.isArray(row)) {
          parsedRows.push(row);
        }
      } catch {
        // Ignore fragments that are not complete encoded JSON objects.
      }
      return parsedRows;
    }, []);

    return rows.length ? rows : value;
  }

  private mapCellJsonRows(cellValue: any, action: any): any[] {
    const sourceRows = Array.isArray(cellValue) ? cellValue : [cellValue];
    const fields = Array.isArray(action?.detailFields) ? action.detailFields : [];
    if (!fields.length) {
      return sourceRows;
    }

    return sourceRows.map((sourceRow: any) => fields.reduce((mappedRow: any, field: any) => {
      const fieldKey = field?.key;
      if (!fieldKey) {
        return mappedRow;
      }

      const candidateKeys = [fieldKey, ...(Array.isArray(field?.keys) ? field.keys : [])];
      const sourceEntries = sourceRow && typeof sourceRow === 'object' ? Object.entries(sourceRow) : [];
      const matchingEntry = sourceEntries.find(([sourceKey]) =>
        candidateKeys.some((candidate: any) =>
          this.normalizeOrderToken(candidate) === this.normalizeOrderToken(sourceKey),
        ),
      );
      const rawValue = matchingEntry?.[1] ?? '';
      const displayValue = field?.controlType === 'date'
        ? this.formatCellJsonDate(rawValue)
        : this.formatCellValue(rawValue);
      mappedRow[fieldKey] = displayValue;
      mappedRow[`${fieldKey}_control_type`] = field?.controlType || 'text';
      mappedRow[`${fieldKey}_options`] = displayValue === ''
        ? []
        : [{ label: displayValue, value: displayValue }];
      return mappedRow;
    }, {}));
  }

  private enrichCellDialogRows(rows: any[], record: any, action: any): any[] {
    const enrichment = action?.rowEnrichment;
    const recordKeys = Array.isArray(enrichment?.recordKeys) ? enrichment.recordKeys : [];
    const matchKey = String(enrichment?.matchKey || '').trim();
    const fieldKeys = Array.isArray(enrichment?.fieldKeys) ? enrichment.fieldKeys : [];
    if (!Array.isArray(rows) || !recordKeys.length || !matchKey || !fieldKeys.length) {
      return rows;
    }

    const rawSource = this.pickCustomValueByCandidateKeys(record, recordKeys);
    const parsedSource = this.parseRowJsonValue(rawSource);
    const sourceRows = Array.isArray(parsedSource)
      ? parsedSource
      : parsedSource && typeof parsedSource === 'object'
        ? [parsedSource]
        : [];

    return rows.map((row: any) => {
      const matchValue = String(this.getCellDialogRowValue(row, matchKey) ?? '').trim();
      if (!matchValue) {
        return row;
      }

      const sourceRow = sourceRows.find((candidate: any) =>
        String(this.getCellDialogRowValue(candidate, matchKey) ?? '').trim() === matchValue,
      );
      if (!sourceRow) {
        return row;
      }

      fieldKeys.forEach((fieldKey: any) => {
        const key = String(fieldKey || '').trim();
        const currentValue = this.getCellDialogRowValue(row, key);
        if (!key || (currentValue !== null && currentValue !== undefined && String(currentValue).trim() !== '')) {
          return;
        }

        const sourceValue = this.getCellDialogRowValue(sourceRow, key);
        if (sourceValue !== null && sourceValue !== undefined) {
          this.setCellDialogRowValue(row, key, sourceValue);
        }
      });

      return row;
    });
  }

  private saveQuoteBillToShipToRowsLocally(): void {
    const fieldKey = String(this.activeCellDialogConfig?.localFormFieldKey || '').trim();
    if (!fieldKey || !this.activeCellDialogRecord || typeof this.activeCellDialogRecord !== 'object') {
      this.cellDialogSaveError = 'Unable to update the Quote form field.';
      return;
    }

    const rows = (Array.isArray(this.activeCellDialogRows) ? this.activeCellDialogRows : [])
      .filter((row: any) => this.hasCellDialogRequestValues(row))
      .map((row: any) => ({
        active: this.normalizeLocalQuoteActiveValue(row?.active),
        status: String(row?.status ?? 'true').trim() || 'true',
        bill_to_code: String(row?.bill_to ?? '').trim(),
        ship_to_code: String(row?.ship_to ?? '').trim(),
        override_date: this.normalizeLocalQuoteOverrideDate(row?.override),
      }));

    this.activeCellDialogRecord[fieldKey] = rows;
    this.cellDialogSaveError = '';
    this.closeCellActionDialog();
  }

  private normalizeLocalQuoteActiveValue(value: any): string | null {
    const normalizedValue = String(value ?? '').trim().toLowerCase();
    if (!normalizedValue) {
      return null;
    }
    if (normalizedValue === 'yes') {
      return 'true';
    }
    if (normalizedValue === 'no') {
      return 'false';
    }
    return normalizedValue;
  }

  private normalizeLocalQuoteOverrideDate(value: any): string | null {
    const normalizedDate = this.formatCellJsonDate(value);
    return normalizedDate ? `${normalizedDate}T00:00:00` : null;
  }

  private buildCustomCellDialogRows(action: any, record: any, column: any, cellValue: any): any[] {
    const rowSourceKey = action?.rowsKey || action?.dataKey;
    const rowSource = rowSourceKey ? record?.[rowSourceKey] : null;
    if (Array.isArray(rowSource)) {
      return this.cloneCellActionPayload(rowSource);
    }

    if (Array.isArray(action?.rows)) {
      return this.cloneCellActionPayload(action.rows);
    }

    const fields = Array.isArray(action?.detailFields) ? action.detailFields : [];
    if (action?.rowsSource !== 'fieldDetails' || !fields.length) {
      return [];
    }

    if (action?.rowsLayout === 'fieldsAsColumns') {
      const row = fields.reduce((mappedRow: any, field: any) => {
        const fieldKey = field?.key;
        if (!fieldKey) {
          return mappedRow;
        }

        const rawValue = this.resolveCustomDetailFieldValue(field, record, cellValue);
        const displayValue = field?.controlType === 'date'
          ? this.formatCellJsonDate(rawValue)
          : this.formatCellValue(rawValue);
        mappedRow[fieldKey] = displayValue;
        mappedRow[`${fieldKey}_control_type`] = field?.controlType || 'text';
        mappedRow[`${fieldKey}_options`] = this.resolveCustomCellDialogOptions({
          ...field,
          options: Array.isArray(field?.options) && field.options.length
            ? field.options
            : displayValue === ''
              ? []
              : [{ label: displayValue, value: displayValue }],
        });
        return mappedRow;
      }, {});
      return [row];
    }

    return fields.map((field: any, index: number) => {
      const rawValue = this.resolveCustomDetailFieldValue(field, record, cellValue);
      const displayValue = field?.controlType === 'date'
        ? this.formatCellJsonDate(rawValue)
        : this.formatCellValue(rawValue);
      return {
        serial: index + 1,
        attribute: field?.label || this.formatColumnLabel(field?.key),
        last_updated_on: this.pickCustomValueByCandidateKeys(record, action?.updatedAtKeys) || '',
        prior_value: displayValue,
        new_value: displayValue,
        value: displayValue,
        action: field?.actionLabel || action?.deleteButtonLabel || 'Delete',
        control_type: field?.controlType || 'text',
        options: this.resolveCustomCellDialogOptions({
          ...field,
          options: Array.isArray(field?.options) && field.options.length
            ? field.options
            : displayValue === ''
              ? []
              : [{ label: displayValue, value: displayValue }],
        }),
      };
    });
  }

  private normalizeCustomCellDialogRows(rows: any, columns: any[] = []): any[] {
    if (!Array.isArray(rows)) {
      return [];
    }

    return rows.map((row: any, index: number) => {
      if (!row || typeof row !== 'object' || Array.isArray(row)) {
        return { __dialogRowId: `cell-dialog-row-${index}`, value: this.formatCellValue(row) };
      }

      const normalizedRow = Object.entries(row).reduce((acc: any, [key, value]) => {
        acc[key] = this.normalizeCustomCellDialogValue(value, key);
        return acc;
      }, {});

      const sourceRowId = row.__dialogRowId ?? row.uuid ?? row.id ?? '';
      normalizedRow.__dialogRowId = `cell-dialog-row-${index}-${String(sourceRowId)}`;
      columns.forEach((column: any) => {
        const columnKey = column?.key;
        if (!columnKey || column?.type === 'button') {
          return;
        }

        const controlType = column.controlType || normalizedRow[`${columnKey}_control_type`] || 'text';
        normalizedRow[`${columnKey}_control_type`] = controlType;
        if (normalizedRow[columnKey] === undefined || normalizedRow[columnKey] === null) {
          const normalizedColumnKey = this.normalizeOrderToken(columnKey);
          const matchingSourceKey = Object.keys(normalizedRow).find((rowKey) =>
            this.normalizeOrderToken(rowKey) === normalizedColumnKey,
          );
          normalizedRow[columnKey] = matchingSourceKey
            ? normalizedRow[matchingSourceKey]
            : '';
        }
        if (controlType === 'date') {
          normalizedRow[columnKey] = this.formatCellJsonDate(normalizedRow[columnKey]);
        }
        if (controlType === 'select') {
          normalizedRow[`${columnKey}_options`] = this.ensureCellDialogSelectOptions(
            normalizedRow[`${columnKey}_options`],
            column.options,
            normalizedRow[columnKey],
          );
        }
      });

      return normalizedRow;
    });
  }

  private normalizeCustomCellDialogValue(value: any, key?: string): any {
    if (value === null || value === undefined) {
      return '';
    }

    if (key && key.endsWith('_options')) {
      return this.resolveCustomCellDialogOptions({ options: Array.isArray(value) ? value : [] });
    }

    if (value instanceof Date) {
      return this.formatCellJsonDate(value);
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.normalizeCustomCellDialogValue(item)).join(', ');
    }

    if (typeof value === 'object') {
      return this.formatCellValue(value);
    }

    return value;
  }

  private normalizeCustomCellDialogColumns(action: any): any[] {
    const tableColumns = Array.isArray(action?.tableColumns)
      ? this.cloneCellActionPayload(action.tableColumns)
      : [];

    return tableColumns.map((column: any) => ({
      ...column,
      options: this.resolveCustomCellDialogOptions(column),
    }));
  }

  private buildCustomCellDialogColumnsFromRows(rows: any[]): any[] {
    const columnKeys: string[] = [];
    const seenKeys = new Set<string>();

    (Array.isArray(rows) ? rows : []).forEach((row: any) => {
      if (!row || typeof row !== 'object' || Array.isArray(row)) {
        return;
      }

      Object.keys(row).forEach((key) => {
        if (
          !key ||
          key.startsWith('__') ||
          key.endsWith('_options') ||
          key.endsWith('_control_type') ||
          seenKeys.has(key)
        ) {
          return;
        }
        seenKeys.add(key);
        columnKeys.push(key);
      });
    });

    return columnKeys.map((key) => ({
      key,
      label: this.formatColumnLabel(key),
      controlType: 'text',
      readonly: true,
      options: [],
    }));
  }

  private ensureCellDialogSelectOptions(rowOptions: any, columnOptions: any, value: any): any[] {
    const options = [
      ...(Array.isArray(columnOptions) ? columnOptions : []),
      ...(Array.isArray(rowOptions) ? rowOptions : []),
    ];
    const normalizedOptions = this.resolveCustomCellDialogOptions({ options });
    const textValue = String(value ?? '').trim();
    if (textValue && !normalizedOptions.some((option) => String(option.value) === textValue)) {
      normalizedOptions.unshift({ label: textValue, value: textValue });
    }
    return normalizedOptions;
  }

  private resolveCustomDetailFieldValue(field: any, record: any, cellValue: any): any {
    const candidateKeys = [
      field?.key,
      ...(Array.isArray(field?.keys) ? field.keys : []),
    ].filter((value: any) => value !== null && value !== undefined);

    for (const source of [record, cellValue]) {
      const resolved = this.pickCustomValueByCandidateKeys(source, candidateKeys);
      if (resolved !== undefined) {
        return resolved;
      }
    }

    return '';
  }

  private pickCustomValueByCandidateKeys(source: any, candidateKeys: any): any {
    const keys = Array.isArray(candidateKeys) ? candidateKeys : [];
    if (!source || typeof source !== 'object' || Array.isArray(source) || !keys.length) {
      return undefined;
    }

    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        return source[key];
      }
    }

    const entries = Object.entries(source);
    for (const key of keys) {
      const normalizedKey = this.normalizeOrderToken(key);
      const match = entries.find(([sourceKey]) => this.normalizeOrderToken(sourceKey) === normalizedKey);
      if (match) {
        return match[1];
      }
    }

    return undefined;
  }

  private interpolateCustomCellActionTitle(template: string, context: any): string {
    return String(template || '').replace(/\{\{\s*([^}]+)\s*\}\}/g, (_match, expression) => {
      const path = String(expression || '').trim().split('.');
      let value = context;
      for (const part of path) {
        value = value?.[part];
      }

      return value === null || value === undefined || value === ''
        ? String(context?.fallbackId || '')
        : String(value);
    });
  }

  private cloneCellActionPayload(payload: any): any {
    if (!payload || typeof payload !== 'object') {
      return payload;
    }

    return JSON.parse(JSON.stringify(payload));
  }

  private formatCellJsonDate(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    if (typeof value === 'object' && !(value instanceof Date)) {
      return '';
    }
    const textValue = String(value ?? '').trim();
    const dayFirstMatch = textValue.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (dayFirstMatch) {
      return `${dayFirstMatch[3]}-${dayFirstMatch[2]}-${dayFirstMatch[1]}`;
    }
    const parsedDate = new Date(textValue);
    return textValue && !Number.isNaN(parsedDate.getTime())
      ? parsedDate.toISOString().slice(0, 10)
      : '';
  }

  private refreshCellDialogEmptyRows(): void {
    const pageSize = Number(this.activeCellDialogPageSize) || 0;
    const rowCount = Array.isArray(this.activeCellDialogRows) ? this.activeCellDialogRows.length : 0;
    const emptyRowCount = Math.max(pageSize - rowCount, 0);
    this.activeCellDialogEmptyRows = Array.from({ length: emptyRowCount }, (_value, index) => index);
  }

  private mapApprovalRows(approvalData: any): Array<{ date: string; level: number | string; user: string; email: string; status: string }> {
    const levels = Array.isArray(approvalData)
      ? approvalData
      : Array.isArray(approvalData?.approvalLevel)
        ? approvalData.approvalLevel
        : [];

    return levels.flatMap((level: any) => {
      const directApproverValue = this.parseRowJsonValue(
        this.getApprovalField(level, ['approvers', 'approver']),
      );
      const userDetails = this.parseRowJsonValue(
        this.getApprovalField(level, ['userDetails', 'user_details']),
      );
      const nestedApproverValue = this.parseRowJsonValue(
        this.getApprovalField(userDetails, ['approvers', 'approver']),
      );
      const directApprovers = Array.isArray(directApproverValue)
        ? directApproverValue
        : directApproverValue ? [directApproverValue] : [];
      const nestedApprovers = Array.isArray(nestedApproverValue)
        ? nestedApproverValue
        : nestedApproverValue ? [nestedApproverValue] : [];
      const users = directApprovers.length > 0
        ? directApprovers
        : nestedApprovers.length > 0
          ? nestedApprovers
          : [this.getApprovalField(level, ['user', 'userName', 'email']) || '-'];

      return users.map((user: any) => {
        const userLabel = this.getApprovalUserLabel(user);
        const approvalName =
          this.getApprovalField(level, ['approval_name', 'approvalName', 'approver_name', 'approverName']) ??
          this.getApprovalField(user, ['approval_name', 'approvalName', 'approver_name', 'approverName']) ??
          this.getApprovalField(level, ['level']) ??
          '-';
        const approvalStatus =
          this.getApprovalField(level, ['approval_status', 'approvalStatus', 'aprroval_status', 'aprrovalStatus', 'status']) ||
          this.getApprovalField(user, ['approval_status', 'approvalStatus', 'aprroval_status', 'aprrovalStatus', 'status']) ||
          this.getApprovalField(approvalData, ['approval_status', 'approvalStatus', 'aprroval_status', 'aprrovalStatus', 'status']) ||
          'UNKNOWN';
        return {
          date: this.formatApprovalDate(
            this.getApprovalField(level, ['approval_date', 'approvalDate', 'date', 'createdDate', 'created_date']) ||
            this.getApprovalField(approvalData, ['date', 'createdDate', 'created_date']),
          ),
          level: approvalName,
          user: userLabel,
          email: this.getApprovalUserEmail(user, userDetails, level, userLabel),
          status: String(approvalStatus).toUpperCase(),
        };
      });
    });
  }

  private getApprovalUserLabel(user: any): string {
    if (user && typeof user === 'object' && !Array.isArray(user)) {
      const label = this.getApprovalField(user, ['user', 'userName', 'username', 'name', 'displayName', 'code', 'id']);
      return String(label || '-');
    }

    return String(user || '-');
  }

  private getApprovalUserEmail(user: any, userDetails: any, level: any, userLabel: string): string {
    const email =
      this.getApprovalField(user, ['email', 'emailAddress', 'mail', 'userEmail']) ||
      this.getApprovalField(userDetails, ['email', 'emailAddress', 'mail', 'userEmail']) ||
      this.getApprovalField(level, ['email', 'emailAddress', 'mail', 'userEmail']);

    if (email) {
      return String(email);
    }

    return userLabel.includes('@') ? userLabel : '-';
  }

  private getApprovalField(source: any, candidateKeys: string[]): any {
    if (!source || typeof source !== 'object' || Array.isArray(source)) {
      return undefined;
    }

    const entries = Object.entries(source);
    for (const candidateKey of candidateKeys) {
      const normalizedCandidate = this.normalizeOrderToken(candidateKey);
      const match = entries.find(([sourceKey]) =>
        this.normalizeOrderToken(sourceKey) === normalizedCandidate,
      );
      if (match) {
        return match[1];
      }
    }
    return undefined;
  }

  private formatApprovalDate(value: any): string {
    if (value === null || value === undefined || String(value).trim() === '') {
      return '-';
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
  }

  protected override extractRecordsFromResponse(response: any, responseKey?: string | null): any[] {
    const responseEntries = Array.isArray(response?.responseList) ? response.responseList : [];
    const responseKeys = this.resolveEntityResponseKeys(responseKey);
    const keyedContainers = [
      ...responseKeys.flatMap((key) => [
        response?.[key],
        response?.data?.[key],
        ...responseEntries.map((entry: any) => entry?.[key]),
      ]),
      ...responseEntries.flatMap((entry: any) =>
        entry && typeof entry === 'object' && !Array.isArray(entry)
          ? Object.values(entry)
          : [],
      ),
    ];

    for (const keyedContainer of keyedContainers) {
      if (!Array.isArray(keyedContainer)) {
        continue;
      }

      const nestedRows = keyedContainer.flatMap((container: any) => {
        if (Array.isArray(container?.data)) {
          return container.data;
        }

        return container?.data && typeof container.data === 'object'
          ? [container.data]
          : [];
      });

      if (nestedRows.length > 0) {
        return this.normalizeRerAuditRecords(this.unwrapRecordDataRows(nestedRows));
      }

      // GenericDynamicReadServices may return the entity array as direct rows
      // rather than [{ data: [...] }]. The quote-oriented extractor added
      // later must not discard that valid RER Suggested response shape.
      if (
        keyedContainer.length > 0 &&
        keyedContainer.every((row: any) => row && typeof row === 'object' && !Array.isArray(row))
      ) {
        return this.normalizeRerAuditRecords(this.unwrapRecordDataRows(keyedContainer));
      }
    }

    return this.normalizeRerAuditRecords(super.extractRecordsFromResponse(response, responseKey));
  }

  private normalizeRerAuditRecords(records: any[]): any[] {
    if (!RER_PA_HISTORY_ENTITIES.has(this.resolveSelectedEntityKey().trim().toLowerCase())) {
      return records;
    }

    return records.map((record) => mapRerAuditSourceData(record));
  }

  private resolveEntityResponseKeys(responseKey?: string | null): string[] {
    const normalizedKey = String(responseKey || '').trim().toLowerCase();
    if (!normalizedKey) {
      return [];
    }

    const aliases: Record<string, string[]> = {
      custom_rer_suggested: ['cu_suggested_prices_rer_engine'],
      cu_suggested_prices_rer_engine: ['custom_rer_suggested'],
      custom_rer_engine_pricing: ['cu_rer_engine_pricing'],
      cu_rer_engine_pricing: ['custom_rer_engine_pricing'],
    };

    return [normalizedKey, ...(aliases[normalizedKey] || [])];
  }

  getColumnDisplayLabel(column: any): string {
    if (!column || typeof column !== 'object') {
      return '';
    }

    const rawKey = String(column?.key || '').trim();
    const rawLabel = String(column?.label || '').trim();
    const normalizedKey = this.normalizeSchemaKey(rawKey);
    if (RER_PA_HISTORY_ENTITIES.has(this.resolveSelectedEntityKey().trim().toLowerCase())) {
      if (normalizedKey === 'current_price') {
        return 'New Price';
      }
      if (normalizedKey === 'current_effective_date') {
        return 'New Effective Date';
      }
    }
    const sourceLabel = rawLabel || rawKey;

    if (!sourceLabel) {
      return '';
    }

    const shouldNormalizeLabel =
      !rawLabel ||
      rawLabel === rawKey ||
      rawLabel.includes('_') ||
      /^[a-z0-9_]+$/i.test(rawLabel) ||
      /^[a-z]+(?:[A-Z][a-z0-9]*)+$/i.test(rawLabel);

    return shouldNormalizeLabel
      ? this.formatColumnLabel(sourceLabel)
      : rawLabel;
  }

  override formatCellValue(value: any, column?: any): string {
    if (
      this.resolveSelectedEntityKey().trim().toLowerCase() === 'custom_rer_engine_pricing' &&
      this.normalizeSchemaKey(column?.key) === 'fuel_rating_hp' &&
      String(value ?? '').trim().toUpperCase() === 'EMPTY'
    ) {
      // EMPTY is an API sentinel for a missing fuel-rating business key. Keep
      // it in the row model for delete requests, but render it as no UI value.
      return '';
    }

    const domainDisplayValue = this.resolveDomainCellDisplayValue(value, column);
    if (domainDisplayValue !== null) {
      return domainDisplayValue;
    }

    const decimalFormattedValue = this.formatDecimalCellValue(value);
    if (decimalFormattedValue !== null) {
      return decimalFormattedValue;
    }

    const formattedValue = super.formatCellValue(value, column);
    const rewrittenValue = this.rewriteAttributeLabelString(formattedValue);
    return this.stripCodeTimeZone(rewrittenValue, column);
  }

  private resolveDomainCellDisplayValue(value: any, column?: any): string | null {
    const columnKey = this.normalizeSchemaKey(column?.key);
    if (!columnKey || !this.domainAttributeColumnKeys?.has(columnKey)) {
      return null;
    }

    if (value === null || value === undefined || value === '') {
      return '';
    }

    if (typeof value === 'object') {
      const data = value?.data ?? value;
      const code = data?.code ?? data?.Code ?? value?.code ?? value?.Code;
      const label = data?.label ?? value?.label;
      if (code !== undefined && code !== null && code !== '') {
        const name = data?.name ?? data?.display_name ?? data?.displayName ??
          value?.name ?? value?.display_name ?? value?.displayName;
        return this.formatDomainCellValue(String(code), name, String(code));
      }

      const scalarDisplayValue =
        data?.name ?? data?.display_name ?? data?.displayName ?? label ??
        data?.value ?? value?.value;
      if (
        scalarDisplayValue !== undefined &&
        scalarDisplayValue !== null &&
        typeof scalarDisplayValue !== 'object'
      ) {
        return String(scalarDisplayValue);
      }
    }

    const lookup = this.domainValueLookupByColumn?.get(columnKey);
    if (lookup) {
      for (const candidate of this.getDomainLookupCandidates(value)) {
        const domainValue = lookup.get(candidate);
        if (domainValue) {
          return this.formatDomainCellValue(
            domainValue.code,
            domainValue.name,
            String(value).trim(),
          );
        }
      }
    }

    const scalarValue = String(value).trim();
    const codeNameMatch = scalarValue.match(/^([^{}]+?)\s*\{([^{}]+)\}\s*$/);
    return codeNameMatch
      ? this.formatDomainCellValue(codeNameMatch[1].trim(), codeNameMatch[2].trim(), scalarValue)
      : scalarValue;
  }

  private formatDomainCellValue(code: string, name: any, fallback: string): string {
    const normalizedName = String(name ?? '').trim();
    if (this.displayFormat === 'code') {
      return code || fallback;
    }
    if (this.displayFormat === 'name') {
      return normalizedName || code || fallback;
    }
    return code && normalizedName ? `${code}{${normalizedName}}` : code || normalizedName || fallback;
  }

  private isRerEnginePricingEntity(): boolean {
    return EntityManagementComponent.RER_ENGINE_PRICING_ENTITY_KEYS.has(
      this.resolveSelectedEntityKey().trim().toLowerCase(),
    );
  }

  private getDomainLookupCandidates(value: any): string[] {
    const candidates = new Set<string>();
    const queue = [value];
    const seen = new WeakSet<object>();
    const comparableKeys = new Set([
      'code', 'id', 'value', 'name', 'label', 'displayname', 'display_name',
    ]);

    while (queue.length > 0) {
      const candidate = queue.shift();
      if (candidate === null || candidate === undefined || candidate === '') {
        continue;
      }
      if (typeof candidate !== 'object') {
        const normalized = this.normalizeDomainLookupValue(candidate);
        if (normalized) {
          candidates.add(normalized);
        }
        continue;
      }
      if (seen.has(candidate)) {
        continue;
      }
      seen.add(candidate);
      const data = candidate?.data ?? candidate;
      Object.entries(data).forEach(([key, nestedValue]) => {
        if (comparableKeys.has(String(key).trim().toLowerCase())) {
          queue.push(nestedValue);
        }
      });
    }

    return Array.from(candidates);
  }

  private normalizeDomainLookupValue(value: any): string {
    return String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  }

  private stripCodeTimeZone(value: string, column?: any): string {
    const columnKey = String(column?.key || '').trim().toLowerCase();
    if (columnKey !== 'code') {
      return value;
    }

    if (this.resolveSelectedEntityKey().toLowerCase() !== RER_AUDIT_ENTITY) {
      return value;
    }

    // Drop the time/timezone portion of any embedded ISO timestamp, keeping the date.
    return value.replace(/(\d{4}-\d{2}-\d{2})T[\d:]+(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?/g, '$1');
  }

  override getDropdownOptionLabel(dropdown: any, option: any): string {
    const formattedLabel = super.getDropdownOptionLabel(dropdown, option);
    if (this.shouldPreserveDropdownLabel(dropdown)) {
      return formattedLabel;
    }

    return this.rewriteAttributeLabelString(formattedLabel);
  }

  override getDropdownSelectedLabel(dropdown: any, selectedValue: any): string {
    if (String(dropdown?.key || '').trim().toLowerCase() === 'entity') {
      const selectedEntity = this.normalizePalantirEntityKey(
        this.getDropdownOptionValue(dropdown, selectedValue),
      ).trim().toLowerCase();

      if (RER_PA_HISTORY_ENTITIES.has(selectedEntity)) {
        return 'RER PA History';
      }
    }

    return super.getDropdownSelectedLabel(dropdown, selectedValue);
  }

  override handleHeaderAction(actionId: string, action?: any): void {
    if (this.isPaHistoryWriteAction(actionId)) {
      return;
    }

    if (actionId === 'history') {
      this.openHistoryDialog(action);
      return;
    }

    if (actionId === 'upload') {
      this.openConfiguredUploadPicker(action);
      return;
    }

    if (actionId === 'delete') {
      this.openDeleteConfirmation();
      return;
    }

    if (actionId !== 'add') {
      if (
        actionId === 'refresh' &&
        RER_PA_HISTORY_ENTITIES.has(this.resolveSelectedEntityKey().trim().toLowerCase())
      ) {
        this.requestedEntityCounts.delete(EntityManagementComponent.RER_AUDIT_ENTITY);
      }
      super.handleHeaderAction(actionId, action);
      return;
    }

    const newRecord: Record<string, any> = {};
    this.filterHiddenColumns();
    const columns = Array.isArray(this.data?.columns) ? this.data.columns : [];
    columns.forEach((column: any) => {
      if (column?.key && column.key !== 'id') {
        newRecord[column.key] = null;
      }
    });
    newRecord['id'] = null;
    newRecord['__isDraftNewRecord'] = true;

    this.debugLog('add_record_initialized', {
      columnKeys: columns.map((column: any) => column?.key).filter(Boolean),
      newRecord,
    });

    this.selectRecord(newRecord);
  }

  shouldShowHeaderAction(action: any): boolean {
    return this.actionMatchesCurrentEntity(action);
  }

  private openConfiguredUploadPicker(action?: any): void {
    const uploadConfig = this.getEntityActionConfig(action, action?.uploadApi);
    if (!uploadConfig?.apiUrl) {
      super.handleHeaderAction('upload', action);
      return;
    }

    const input = this.headerUploadInput?.nativeElement;
    if (!input) {
      return;
    }

    this.pendingUploadAction = action;
    input.accept = uploadConfig?.accept || action?.accept || '.xls,.xlsx,.xlsm';
    input.value = '';
    input.click();
  }

  onHeaderUploadFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const action = this.pendingUploadAction;
    const uploadConfig = this.getEntityActionConfig(action, action?.uploadApi);

    if (!file || !uploadConfig?.apiUrl) {
      return;
    }

    const fields = this.cloneCustomPayload(uploadConfig?.fields || action?.fields || {});
    const formData = new FormData();
    Object.keys(fields).forEach((key) => formData.append(key, fields[key]));
    formData.append(uploadConfig?.fileField || 'file', file, file.name);

    const token = this.keycloakService.getKeycloakInstance()?.token;
    const headers: Record<string, string> = {
      ...(uploadConfig?.headers || action?.headers || {}),
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    this.loading = true;
    this.http.post(this.customConfigService.resolveUrl(uploadConfig.apiUrl), formData, {
      responseType: 'text',
      headers,
    }).pipe(
      finalize(() => {
        this.loading = false;
        this.pendingUploadAction = null;
        input.value = '';
        this.customCdr.detectChanges();
      })
    ).subscribe({
      next: () => {
        this.showConfiguredActionMessage(uploadConfig?.successMessage || 'File uploaded successfully.', 'success-snackbar');
        this.loadRecords().subscribe();
      },
      error: (error) => {
        console.error('Upload failed', error);
        this.showConfiguredActionMessage(uploadConfig?.errorMessage || 'Upload failed.', 'error-snackbar');
      },
    });
  }

  private downloadConfiguredFile(action: any): void {
    const downloadConfig = this.getEntityActionConfig(action, action?.downloadApi);
    if (!downloadConfig?.apiUrl) {
      super.handleHeaderAction('download', action);
      return;
    }

    const payload = this.resolveDownloadPayload(
      this.cloneCustomPayload(downloadConfig?.payload || action?.payload || {}),
    );
    const fileName = downloadConfig?.fileName || payload?.filename || `${this.resolveSelectedEntityKey() || 'entity-records'}.xlsm`;
    const token = this.keycloakService.getKeycloakInstance()?.token;
    const headers: Record<string, string> = {
      ...(downloadConfig?.headers || action?.headers || {}),
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    this.loading = true;
    this.http.post(this.customConfigService.resolveUrl(downloadConfig.apiUrl), payload, {
      responseType: 'blob',
      headers,
    }).pipe(
      switchMap((response) => from(this.extractDownloadFile(response))),
      finalize(() => {
        this.loading = false;
        this.customCdr.detectChanges();
      })
    ).subscribe({
      next: (download) => this.saveBlob(download.blob, download.fileName || fileName),
      error: (error) => {
        console.error('Download failed', error);
        this.showConfiguredActionMessage(downloadConfig?.errorMessage || 'Download failed.', 'error-snackbar');
      },
    });
  }

  private async extractDownloadFile(response: Blob): Promise<{ blob: Blob; fileName?: string }> {
    const contentType = response.type.toLowerCase();
    if (!contentType.includes('json')) {
      return { blob: response };
    }

    const responseText = await response.text();
    let responseBody: any;
    try {
      responseBody = JSON.parse(responseText);
    } catch {
      return { blob: response };
    }

    const fileContent = responseBody?.data?.fileContent;
    if (typeof fileContent !== 'string' || !fileContent) {
      throw new Error('Download response does not contain data.fileContent');
    }

    const base64Content = fileContent.includes(',')
      ? fileContent.substring(fileContent.indexOf(',') + 1)
      : fileContent;
    const binaryContent = atob(base64Content);
    const bytes = new Uint8Array(binaryContent.length);
    for (let index = 0; index < binaryContent.length; index++) {
      bytes[index] = binaryContent.charCodeAt(index);
    }

    return {
      blob: new Blob([bytes], {
        type: 'application/vnd.ms-excel.sheet.macroEnabled.12',
      }),
      fileName: String(responseBody?.data?.fileName || '').trim() || undefined,
    };
  }

  private resolveDownloadPayload(payload: any): any {
    const filterValues = payload?.entity === 'custom_rer_engine_pricing'
      ? this.getEnginePricingDownloadFilterValues()
      : this.getDownloadFilterValues();
    const resolvedFilterValues = Object.keys(filterValues).length > 0
      ? filterValues
      : { uuid: null };
    const replaceTokens = (value: any): any => {
      if (typeof value !== 'string') {
        if (Array.isArray(value)) {
          return value.map(replaceTokens);
        }
        if (value && typeof value === 'object') {
          Object.keys(value).forEach((key) => {
            value[key] = replaceTokens(value[key]);
          });
        }
        return value;
      }

      const tokenMatch = value.match(/^\{\{\s*([^}]+?)\s*\}\}$/);
      if (tokenMatch) {
        const tokenKey = tokenMatch[1].trim();
        if (tokenKey === 'appliedFilterValues') {
          return resolvedFilterValues;
        }
        return filterValues[tokenKey] ?? value;
      }

      return value.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_token, tokenKey) => {
        const resolvedValue = filterValues[tokenKey.trim()];
        return resolvedValue === undefined || resolvedValue === null
          ? ''
          : String(resolvedValue);
      });
    };

    return replaceTokens(payload);
  }

  private getEnginePricingDownloadFilterValues(): Record<string, any[]> {
    const filterValues: Record<string, any[]> = { is_deleted: [false] };
    const appliedFilters = (this as any).appliedDialogFilterPayload?.requestData?.filters;
    if (!Array.isArray(appliedFilters)) {
      Object.entries(this.getDownloadFilterValues()).forEach(([key, value]) => {
        if (key.trim().toLowerCase() === 'uuid_not_null') {
          return;
        }
        filterValues[key] = Array.isArray(value) ? [...value] : [value];
      });
      return filterValues;
    }

    const suffixes: Record<string, string> = {
      '': '', '=': '', '==': '', is: '', equal: '', is_equal: '', is_equal_to: '',
      '!=': '_not', '<>': '_not', is_not: '_not', not_equal: '_not',
      is_not_equal: '_not', is_not_equal_to: '_not',
      is_null: '_null', is_not_null: '_not_null',
      in: '_in', not_in: '_not_in',
      '<': '_less_than', less_than: '_less_than',
      '>': '_greater_than', greater_than: '_greater_than',
      '<=': '_less_than_or_equal', less_than_or_equal: '_less_than_or_equal',
      '>=': '_greater_than_or_equal', greater_than_or_equal: '_greater_than_or_equal',
      contains: '_contains',
    };
    appliedFilters.forEach((filter: any) => {
      const key = String(filter?.attributeName || '').trim();
      const operator = String(filter?.operator || '').trim().toLowerCase().replace(/\s+/g, '_');
      if (!this.isDownloadFilterKey(key)
        || key.toLowerCase() === 'uuid_not_null'
        || (key.toLowerCase() === 'uuid' && operator === 'is_not_null')
        || !Object.prototype.hasOwnProperty.call(suffixes, operator)) {
        return;
      }
      const isNullOperator = operator === 'is_null' || operator === 'is_not_null';
      if (!isNullOperator && (filter.attributeValue === undefined || filter.attributeValue === null)) {
        return;
      }
      filterValues[key + suffixes[operator]] = isNullOperator
        ? [null]
        : Array.isArray(filter.attributeValue) ? [...filter.attributeValue] : [filter.attributeValue];
    });
    return filterValues;
  }

  private getDownloadFilterValues(): Record<string, any> {
    const appliedFilters = (this as any).appliedDialogFilterPayload?.requestData?.filters;
    if (Array.isArray(appliedFilters)) {
      return appliedFilters.reduce((filterValues: Record<string, any>, filter: any) => {
        const attributeName = String(filter?.attributeName || '').trim();

        if (attributeName && this.isDownloadFilterKey(attributeName) && filter?.attributeValue !== undefined && filter?.attributeValue !== null) {
          filterValues[attributeName] = Array.isArray(filter.attributeValue)
            ? filter.attributeValue
            : [filter.attributeValue];
        }
        return filterValues;
      }, {});
    }

    const globalFilters = this.customGlobalFilterService.getCurrentFilters?.() || {};
    return this.sanitizeDownloadFilterValues({
      ...(globalFilters?.requestParams || {}),
      ...(globalFilters?.queryParams || {}),
      ...(this.currentFilters || {}),
    });
  }

  private sanitizeDownloadFilterValues(filters: Record<string, any>): Record<string, any> {
    return Object.entries(filters || {}).reduce<Record<string, any>>((cleaned, [key, value]) => {
      if (!this.isDownloadFilterKey(key) || value === undefined || value === null || value === '') {
        return cleaned;
      }

      if (Array.isArray(value) && value.length === 0) {
        return cleaned;
      }

      cleaned[key] = value;
      return cleaned;
    }, {});
  }

  private isDownloadFilterKey(key: string): boolean {
    const normalizedKey = String(key || '').trim().toLowerCase().replace(/[_\s-]+/g, '');
    const excludedKeys = new Set([
      'loading',
      'entity',
      'entityid',
      'entitydisplayname',
      'model',
      'modelid',
      'modelname',
      'sourceapp',
      'selectedentitylabel',
      'offset',
      'limit',
      'pagesize',
      'page',
      'currentpage',
      'orderby',
      'order',
      'sort',
      'sortby',
    ]);

    return Boolean(normalizedKey)
      && !excludedKeys.has(normalizedKey)
      && !normalizedKey.startsWith('widget');
  }

  private getEntityActionConfig(action: any, config: any): any {
    if (!config) {
      return null;
    }
    if (!Array.isArray(config?.byEntity)) {
      return this.actionMatchesCurrentEntity(config) ? config : null;
    }

    const selectedEntity = this.normalizeActionEntity(this.resolveSelectedEntityKey());
    return config.byEntity.find((item: any) =>
      this.getActionEntities(item).some((entity) => this.normalizeActionEntity(entity) === selectedEntity)
    ) || null;
  }

  private actionMatchesCurrentEntity(action: any): boolean {
    const entities = this.getActionEntities(action);
    if (!entities.length) {
      return true;
    }

    const selectedEntity = this.normalizeActionEntity(this.resolveSelectedEntityKey());
    return entities.some((entity) => this.normalizeActionEntity(entity) === selectedEntity);
  }

  private getActionEntities(action: any): any[] {
    return Array.isArray(action?.entities)
      ? action.entities
      : Array.isArray(action?.entityValues)
        ? action.entityValues
        : [];
  }

  private normalizeActionEntity(value: any): string {
    return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  private cloneCustomPayload<T>(payload: T): T {
    if (!payload || typeof payload !== 'object') {
      return payload;
    }

    return JSON.parse(JSON.stringify(payload));
  }

  private saveBlob(blob: Blob, fileName: string): void {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  private showConfiguredActionMessage(message: string, panelClass: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 4000,
      horizontalPosition: 'right',
      verticalPosition: 'bottom',
      panelClass: [panelClass],
    });
  }

  override isHeaderActionDisabled(action: any): boolean {
    if (this.isPaHistoryWriteAction(action)) {
      return true;
    }

    if (action?.actionId === 'clear_filter') {
      return !this.hasDialogFilterApplied;
    }

    return super.isHeaderActionDisabled(action);
  }

  isPaHistoryWriteAction(action: any): boolean {
    const actionId = String(typeof action === 'string' ? action : action?.actionId || '').trim().toLowerCase();
    return RER_PA_HISTORY_ENTITIES.has(this.resolveSelectedEntityKey().trim().toLowerCase()) &&
      (actionId === 'add' || actionId === 'delete');
  }

  override onDropdownSelectionChange(dropdownKey: any, selectedValue: any): any {
    const dropdown =
      this.data?.header?.dropdowns?.find((item: any) => item.key === dropdownKey) ||
      { key: dropdownKey };
    const option = this.getSelectedDropdownOption(dropdown, selectedValue);
    const resolvedValue = this.resolveSelectedDropdownValue(
      dropdown,
      this.getDropdownOptionValue(dropdown, selectedValue),
    );
    const normalizedValue = String(dropdownKey || '').trim().toLowerCase() === 'entity'
      ? this.normalizePalantirEntityKey(resolvedValue)
      : resolvedValue;
    const normalizedLabel = option
      ? this.getDropdownOptionLabel(dropdown, option)
      : this.getDropdownSelectedLabel(dropdown, selectedValue);

    this.debugLog('dropdown_selection_change', {
      dropdownKey,
      rawSelectedValue: selectedValue,
      normalizedValue,
      normalizedLabel,
      currentFiltersBeforeUpdate: this.currentFilters,
    });

    this.currentFilters[dropdownKey] = normalizedValue;
    if (String(dropdownKey || '').trim().toLowerCase() === 'entity') {
      this.domainAttributeColumnKeys.clear();
      this.domainValueLookupByColumn.clear();
    }
    if (
      String(dropdownKey || '').trim().toLowerCase() === 'entity' &&
      EntityManagementComponent.SERVER_PAGINATED_ENTITY_KEYS.has(
        String(normalizedValue || '').trim().toLowerCase(),
      )
    ) {
      this.currentPage = 1;
      this.serverLastPageByEntity.delete(String(normalizedValue || '').trim().toLowerCase());
      this.updatePayloadOffset(this.data?.customPayload, 0);
    }
    this.syncHeaderTitleFromDropdown(dropdownKey);
    this.syncSelectedEntityLabel(dropdownKey);

    if (this.routeToProjectEntity(dropdownKey, normalizedValue)) {
      return;
    }

    if (this.handleDropdownRedirect(dropdown, normalizedValue, normalizedLabel)) {
      return;
    }

    this.loadEntityPermissions(normalizedValue);

    // A selection that navigates away must not be broadcast to widgets on the
    // current page. Those widgets would otherwise start entity-specific API and
    // optional header-order requests just before Angular destroys them.
    if (this.data?.widgetId) {
      const isQueryParam = dropdown?.isQueryParam === true || this.data?.method?.toLowerCase() === 'post';
      this.customGlobalFilterService.updateFilterSelection(
        dropdownKey,
        [normalizedValue],
        isQueryParam,
        this.data.widgetId,
      );
    }

    this.data.columns = undefined;
    this.loadRecords().subscribe();
  }

  /** Load permissions only when the Palantir table opens the RER engine-pricing entity. */
  private loadEntityPermissions(entityKey: unknown): void {
    const normalizedEntity = this.normalizePalantirEntityKey(entityKey).trim().toLowerCase();
    if (!EntityManagementComponent.RER_ENGINE_PRICING_ENTITY_KEYS.has(normalizedEntity)) {
      return;
    }

    const baseUrl = String(this.environmentConfig?.API_URL || '').replace(/\/$/, '');
    const url = `${baseUrl}/spriced/auth/getEntityPermissions`;

    this.entityPermissionsRequest?.unsubscribe();
    this.entityPermissionsRequest = this.http.post(url, {
      userRole: ['g_nrp_cpm'],
    }).subscribe({
      error: (error: unknown) =>
        console.error('Unable to load RER engine pricing entity permissions:', error),
    });
  }

  private routeToProjectEntity(dropdownKey: any, selectedValue: any): boolean {
    if (String(dropdownKey || '').trim().toLowerCase() !== 'entity') {
      return false;
    }

    const targetEntity = this.resolveProjectEntityTarget(selectedValue);
    if (!targetEntity) {
      return false;
    }

    const currentPath = String(this.router.url || '').split(/[?#]/)[0].replace(/\/$/, '');
    if (currentPath === '/goms-customer') {
      void this.router.navigate(['/palantir_charts'], {
        queryParams: { entity: targetEntity },
      });
      return true;
    }

    // Keep the URL aligned with the entity shown by the in-page Palantir table.
    // The component is reused for query-param-only navigation, so return false
    // and let the normal selection flow load the new entity locally.
    if (currentPath === '/palantir_charts') {
      void this.router.navigate(['/palantir_charts'], {
        queryParams: { entity: targetEntity },
      });
      return false;
    }

    return false;
  }

  private resolveProjectEntityTarget(selectedValue: any): string | null {
    const entityKey = this.normalizePalantirEntityKey(selectedValue).trim().toLowerCase();
    const projectEntities: Record<string, string> = {
      custom_rer_engine_pricing: 'custom_rer_engine_pricing',
      cu_rer_engine_pricing: 'custom_rer_engine_pricing',
      custom_rer_suggested: 'custom_rer_suggested',
      cu_suggested_prices_rer_engine: 'custom_rer_suggested',
      [RER_AUDIT_ENTITY]: RER_AUDIT_ENTITY,
    };

    return projectEntities[entityKey] || null;
  }

  private handleDropdownRedirect(dropdown: any, selectedValue: any, selectedLabel?: string): boolean {
    // Project-owned entities must always stay in this Angular application.
    // This explicit guard prevents QA response label/value differences from
    // falling through to the configured SPRICED_DATA_URL external redirect.
    if (
      String(dropdown?.key || '').trim().toLowerCase() === 'entity' &&
      this.resolveProjectEntityTarget(selectedValue)
    ) {
      return false;
    }

    const redirectConfig =
      dropdown?.redirect ||
      dropdown?.redirectConfig ||
      dropdown?.externalRedirect;

    if (!redirectConfig || redirectConfig.enabled === false) {
      this.debugLog('redirect_skipped_no_config', {
        dropdownKey: dropdown?.key,
        selectedValue,
        selectedLabel,
      });
      return false;
    }

    const baseUrl = this.resolveRedirectBaseUrl(redirectConfig);
    if (!baseUrl) {
      this.debugLog('redirect_skipped_no_base_url', {
        dropdownKey: dropdown?.key,
        selectedValue,
        selectedLabel,
        redirectConfig,
      });
      return false;
    }

    const resolvedLabel = selectedLabel || this.getDropdownSelectedLabel(dropdown, selectedValue);
    const shouldRedirect = this.shouldRedirectSelectedValue(redirectConfig, selectedValue, resolvedLabel);
    this.debugLog('redirect_decision', {
      dropdownKey: dropdown?.key,
      selectedValue,
      resolvedLabel,
      shouldRedirect,
      redirectConfig,
    });

    if (!shouldRedirect) {
      return false;
    }

    const redirectUrl = this.buildDropdownRedirectUrl(
      baseUrl,
      redirectConfig,
      dropdown,
      selectedValue,
      resolvedLabel,
    );

    if (!redirectUrl) {
      this.debugLog('redirect_skipped_empty_url', {
        dropdownKey: dropdown?.key,
        selectedValue,
        resolvedLabel,
      });
      return false;
    }

    this.debugLog('redirecting', {
      dropdownKey: dropdown?.key,
      selectedValue,
      resolvedLabel,
      redirectUrl,
    });

    window.open(redirectUrl, String(redirectConfig?.target || '_self'));
    return true;
  }

  private shouldRedirectSelectedValue(
    redirectConfig: any,
    selectedValue: any,
    selectedLabel: string,
  ): boolean {
    const selectedEntityKey = this.normalizePalantirEntityKey(selectedValue).trim().toLowerCase();
    const selectedLabelKey = String(selectedLabel || '')
      .trim()
      .toLowerCase()
      .replace(/^\d+\s*-?\s*/, '');
    const isPaHistory =
      RER_PA_HISTORY_ENTITIES.has(selectedEntityKey) ||
      ['pa history', 'rer pa history', 'rer audit'].includes(selectedLabelKey);

    // PA History is hosted by this Angular app and must never use the external
    // Data Explorer redirect, even when its API option arrives as a label alias.
    if (isPaHistory) {
      return false;
    }

    const configuredEntities = Array.isArray(redirectConfig?.entities)
      ? redirectConfig.entities
      : [];

    if (!configuredEntities.length) {
      return false;
    }

    const matchAgainst = String(redirectConfig?.matchAgainst || 'either')
      .trim()
      .toLowerCase();
    const ignoreCase = redirectConfig?.ignoreCase !== false;
    const entityDropdown = this.getDropdownConfigByKey('entity');
    const matchCandidates = this.getRedirectMatchCandidates(
      entityDropdown,
      selectedValue,
      selectedLabel,
      ignoreCase,
    );
    const entityMatchMode = String(redirectConfig?.entityMatchMode || 'include')
      .trim()
      .toLowerCase();

    const hasMatch = configuredEntities.some((entity: any) => {
      const normalizedEntity = this.normalizeRedirectMatchValue(entity, ignoreCase);
      if (!normalizedEntity) {
        return false;
      }

      if (matchAgainst === 'value') {
        return matchCandidates.value.has(normalizedEntity);
      }

      if (matchAgainst === 'label') {
        return matchCandidates.label.has(normalizedEntity);
      }

      return (
        matchCandidates.value.has(normalizedEntity) ||
        matchCandidates.label.has(normalizedEntity)
      );
    });

    this.debugLog('redirect_match_evaluation', {
      selectedValue,
      selectedLabel,
      matchAgainst,
      entityMatchMode,
      configuredEntities,
      valueCandidates: Array.from(matchCandidates.value),
      labelCandidates: Array.from(matchCandidates.label),
      hasMatch,
    });

    return entityMatchMode === 'exclude' ? !hasMatch : hasMatch;
  }

  private buildDropdownRedirectUrl(
    baseUrl: string,
    redirectConfig: any,
    dropdown: any,
    selectedValue: any,
    selectedLabel: string,
  ): string {
    try {
      const redirectUrl = new URL(baseUrl, window.location.origin);
      const queryParamName = String(
        redirectConfig?.queryParamName ||
        redirectConfig?.paramName ||
        dropdown?.key ||
        'entity',
      ).trim();
      const queryParamSource = String(redirectConfig?.queryParamSource || 'value')
        .trim()
        .toLowerCase();
      const queryParamValue =
        queryParamSource === 'label'
          ? selectedLabel
          : this.normalizeRedirectParamValue(selectedValue);

      redirectUrl.searchParams.set(queryParamName, queryParamValue);
      this.appendRedirectContextParams(redirectUrl, dropdown, selectedValue, selectedLabel);

      const additionalQueryParams = redirectConfig?.additionalQueryParams;
      if (additionalQueryParams && typeof additionalQueryParams === 'object') {
        Object.entries(additionalQueryParams).forEach(([key, value]) => {
          if (!key) {
            return;
          }

          redirectUrl.searchParams.set(
            key,
            this.resolveRedirectTemplateValue(value, selectedValue, selectedLabel, dropdown?.key),
          );
        });
      }

      this.debugLog('redirect_url_built', {
        baseUrl,
        queryParamName,
        queryParamSource,
        selectedValue,
        selectedLabel,
        finalUrl: redirectUrl.toString(),
      });

      return redirectUrl.toString();
    } catch (error) {
      console.error('Invalid dropdown redirect URL configuration:', error);
      this.debugLog('redirect_url_build_error', {
        baseUrl,
        selectedValue,
        selectedLabel,
        error,
      });
      return '';
    }
  }

  private normalizeRedirectMatchValue(value: any, ignoreCase: boolean): string {
    const normalized = this.normalizeRedirectParamValue(value);
    return ignoreCase ? normalized.toLowerCase() : normalized;
  }

  private normalizeRedirectParamValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    return typeof value === 'string' ? value : String(value);
  }

  private resolveRedirectTemplateValue(
    value: any,
    selectedValue: any,
    selectedLabel: string,
    dropdownKey: string,
  ): string {
    const normalizedValue = this.normalizeRedirectParamValue(selectedValue);

    if (value === '$value') {
      return normalizedValue;
    }

    if (value === '$label') {
      return selectedLabel;
    }

    if (value === '$key') {
      return dropdownKey || '';
    }

    return this.normalizeRedirectParamValue(value);
  }

  private resolveRedirectBaseUrl(redirectConfig: any): string {
    const envKey = String(
      redirectConfig?.baseUrlEnvKey ||
      redirectConfig?.urlEnvKey ||
      '',
    ).trim();

    if (envKey && this.environmentConfig) {
      const envValue = (this.environmentConfig as Record<string, unknown>)[envKey];
      if (typeof envValue === 'string' && envValue.trim()) {
        this.debugLog('redirect_base_url_from_env', {
          envKey,
          resolvedBaseUrl: envValue.trim(),
        });
        return envValue.trim();
      }
    }

    const fallbackUrl = String(redirectConfig?.baseUrl || redirectConfig?.url || '').trim();
    this.debugLog('redirect_base_url_fallback', {
      envKey,
      resolvedBaseUrl: fallbackUrl,
    });
    return fallbackUrl;
  }

  private appendRedirectContextParams(
    redirectUrl: URL,
    dropdown: any,
    selectedValue: any,
    selectedLabel: string,
  ): void {
    const selectedEntityOption = this.getSelectedDropdownOption(dropdown, selectedValue);
    const currentModelDropdown = this.getDropdownConfigByKey('model');
    const currentModelValue = currentModelDropdown
      ? this.currentFilters?.[currentModelDropdown.key]
      : undefined;
    const selectedModelOption = currentModelDropdown
      ? this.getSelectedDropdownOption(currentModelDropdown, currentModelValue)
      : null;
    const modelLabel = currentModelDropdown
      ? this.getDropdownSelectedLabel(currentModelDropdown, currentModelValue)
      : '';

    redirectUrl.searchParams.set('entity', this.normalizeRedirectParamValue(selectedValue));

    const entityId = this.resolveOptionField(selectedEntityOption, ['id', 'entityId', 'entity_id']);
    if (entityId !== '') {
      redirectUrl.searchParams.set('entityId', entityId);
    }

    if (selectedLabel) {
      redirectUrl.searchParams.set('entityDisplayName', selectedLabel);
    }

    const modelValue = this.normalizeRedirectParamValue(currentModelValue);
    if (modelValue) {
      redirectUrl.searchParams.set('model', modelValue);
    }

    const modelId = this.resolveOptionField(selectedModelOption, ['id', 'modelId', 'model_id']);
    if (modelId !== '') {
      redirectUrl.searchParams.set('modelId', modelId);
    }

    if (modelLabel) {
      redirectUrl.searchParams.set('modelName', modelLabel);
    }

    this.debugLog('redirect_context_params', {
      selectedEntityOption,
      selectedValue,
      selectedLabel,
      currentModelValue,
      selectedModelOption,
      finalSearchParams: redirectUrl.search,
    });
  }

  private getDropdownConfigByKey(dropdownKey: string): any | null {
    const dropdowns = Array.isArray(this.data?.header?.dropdowns)
      ? this.data.header.dropdowns
      : [];

    return dropdowns.find((item: any) => item?.key === dropdownKey) || null;
  }

  private getSelectedDropdownOption(dropdown: any, selectedValue: any): any | null {
    const optionsList = Array.isArray(dropdown?.optionsList) ? dropdown.optionsList : [];
    const exactValueMatch = optionsList.find(
      (option: any) => this.getDropdownOptionValue(dropdown, option) === selectedValue,
    );

    if (exactValueMatch) {
      return exactValueMatch;
    }

    const normalizedSelectedValue = this.normalizeRedirectParamValue(selectedValue).toLowerCase();
    return (
      optionsList.find((option: any) => {
        const optionValue = this.normalizeRedirectParamValue(
          this.getDropdownOptionValue(dropdown, option),
        ).toLowerCase();
        const optionLabel = this.normalizeRedirectParamValue(
          this.getDropdownOptionLabel(dropdown, option),
        ).toLowerCase();

        return optionValue === normalizedSelectedValue || optionLabel === normalizedSelectedValue;
      }) || null
    );
  }

  private resolveOptionField(option: any, keys: string[]): string {
    if (!option || typeof option !== 'object') {
      return '';
    }

    for (const key of keys) {
      const value = option?.[key];
      if (value !== undefined && value !== null && value !== '') {
        return this.normalizeRedirectParamValue(value);
      }
    }

    return '';
  }

  private applyInitialDropdownValuesFromQueryParams(): void {
    const dropdowns = Array.isArray(this.data?.header?.dropdowns)
      ? this.data.header.dropdowns
      : [];

    if (!dropdowns.length) {
      return;
    }

    const seededFilters = this.customGlobalFilterService.getCurrentFilters?.() || {};
    const queryParams = {
      ...(seededFilters?.queryParams || {}),
      ...(this.route?.snapshot?.queryParams || {}),
    } as Record<string, any>;

    dropdowns.forEach((dropdown: any) => {
      const queryParamKey = String(
        dropdown?.queryParamName ||
        dropdown?.redirect?.queryParamName ||
        dropdown?.paramName ||
        dropdown?.key ||
        '',
      ).trim();

      if (!queryParamKey || queryParams[queryParamKey] === undefined || queryParams[queryParamKey] === null) {
        return;
      }

      const rawValue = Array.isArray(queryParams[queryParamKey])
        ? queryParams[queryParamKey][0]
        : queryParams[queryParamKey];
      const normalizedValue = typeof rawValue === 'string'
        ? decodeURIComponent(rawValue)
        : rawValue;

      dropdown.defaultValue = normalizedValue;
      this.currentFilters[dropdown.key] = normalizedValue;
      this.debugLog('initial_query_param_applied', {
        dropdownKey: dropdown?.key,
        queryParamKey,
        rawValue,
        normalizedValue,
      });
    });
  }

  private applySelectedEntityToPayload(entityKey = this.resolveSelectedEntityKey()): void {
    if (!entityKey || !this.data || typeof this.data !== 'object') {
      return;
    }

    // This widget serves multiple entities. Its response key must follow the
    // selected entity; leaving it at the configured engine-pricing default
    // makes responses such as custom_rer_suggested look empty.
    this.data.key = entityKey;

    if (
      this.data?.applySelectedEntityToRequestData !== false &&
      this.data?.customPayload?.requestData &&
      typeof this.data.customPayload.requestData === 'object'
    ) {
      this.data.customPayload.requestData.entity = entityKey;
    }
    this.applySelectedEntityFunctionName(entityKey);

    const headerActions = Array.isArray(this.data?.header?.actions) ? this.data.header.actions : [];
    headerActions.forEach((action: any) => {
      if (
        this.data?.applySelectedEntityToRequestData !== false &&
        action?.defaultApplyPayload?.requestData &&
        typeof action.defaultApplyPayload.requestData === 'object'
      ) {
        action.defaultApplyPayload.requestData.entity = entityKey;
      }

      const functionName = this.resolveSelectedEntityFunctionName(entityKey);
      if (functionName && Array.isArray(action?.defaultApplyPayload?.endpoints)) {
        action.defaultApplyPayload.endpoints = action.defaultApplyPayload.endpoints.map(
          (endpoint: any, index: number) => ({
            ...endpoint,
            functionName: index === 0 ? functionName : endpoint?.functionName,
          }),
        );
        action.submitFunctionName = functionName;
      }
    });

    this.debugLog('payload_entity_applied', {
      entityKey,
      responseKey: this.data?.key,
      customPayloadRequestData: this.data?.customPayload?.requestData,
      loadFunctionName: this.data?.customPayload?.endpoints?.[0]?.functionName,
      actionPayloads: headerActions.map((action: any) => ({
        actionId: action?.actionId,
        entity: action?.defaultApplyPayload?.requestData?.entity,
        functionName: action?.defaultApplyPayload?.endpoints?.[0]?.functionName,
      })),
    });
  }

  private applySelectedEntityFunctionName(entityKey: string): void {
    const customPayload = this.data?.customPayload;
    this.captureEntityFunctionNameOverrides(customPayload);
    const overrides = this.tableEntityFunctionNameOverrides;

    if (!customPayload || !overrides || typeof overrides !== 'object') {
      return;
    }

    const functionName = this.resolveSelectedEntityFunctionName(entityKey);

    if (!functionName || !Array.isArray(customPayload.endpoints)) {
      return;
    }

    customPayload.endpoints = customPayload.endpoints.map((endpoint: any, index: number) => ({
      ...endpoint,
      functionName: index === 0 ? functionName : endpoint?.functionName,
    }));
  }

  private resolveSelectedEntityFunctionName(entityKey: string): string {
    const normalizedEntityKey = String(entityKey || '').trim().toLowerCase();
    const overrideFunctionName = String(
      this.tableEntityFunctionNameOverrides?.[normalizedEntityKey] || '',
    ).trim();
    const configuredFunctionName = overrideFunctionName || this.tableDefaultFunctionName;

    if (
      configuredFunctionName === EntityManagementComponent.QUOTE_DETAIL_FUNCTION &&
      normalizedEntityKey !== EntityManagementComponent.QUOTE_DETAIL_ENTITY
    ) {
      return EntityManagementComponent.DEFAULT_DYNAMIC_READ_FUNCTION;
    }

    return configuredFunctionName;
  }

  private captureEntityFunctionNameOverrides(customPayload: any): void {
    if (!customPayload || typeof customPayload !== 'object') {
      return;
    }

    const configuredDefaultFunctionName =
      customPayload?.defaultFunctionName ?? this.data?.defaultFunctionName;
    if (configuredDefaultFunctionName) {
      this.tableDefaultFunctionName = String(configuredDefaultFunctionName || '').trim();
      // Keep routing metadata outside the API payload. Persisting it on the
      // component config also lets a recreated widget recover the routing
      // contract after the legacy payload properties below are removed.
      this.data.defaultFunctionName = this.tableDefaultFunctionName;
      delete customPayload.defaultFunctionName;
    }

    const configuredOverrides =
      customPayload?.entityFunctionNameOverrides ?? this.data?.entityFunctionNameOverrides;
    if (configuredOverrides && typeof configuredOverrides === 'object') {
      this.tableEntityFunctionNameOverrides = Object.entries(configuredOverrides)
        .reduce((overrides: Record<string, string>, [entityKey, functionName]) => {
          overrides[String(entityKey || '').trim().toLowerCase()] = String(functionName || '').trim();
          return overrides;
        }, {});
      delete customPayload.entityFunctionNameOverrides;
    }
  }

  private resolveSelectedEntityKey(dropdown = this.getDropdownConfigByKey('entity')): string {
    if (!dropdown) {
      return this.normalizePalantirEntityKey(
        this.data?.selectedEntity ||
        this.currentFilters?.entity ||
        this.route?.snapshot?.queryParams?.['entity'] ||
        '',
      );
    }

    return this.normalizePalantirEntityKey(
      this.resolveSelectedDropdownValue(dropdown, this.currentFilters?.[dropdown.key]),
    );
  }

  private normalizePalantirEntityKey(value: any): string {
    const normalizedValue = this.normalizeRedirectParamValue(value).trim();
    const normalizedToken = normalizedValue.toLowerCase();
    const displayLabelToken = normalizedToken.replace(/^\d+\s*-?\s*/, '');
    const aliases: Record<string, string> = {
      'pa history': RER_AUDIT_ENTITY,
      'rer audit': RER_AUDIT_ENTITY,
      'rer pa history': RER_AUDIT_ENTITY,
      'rer suggested price': 'custom_rer_suggested',
      cu_suggested_prices_rer_engine: 'custom_rer_suggested',
      's_goms customer': 'cu_supp_s_goms_customer',
      s_goms_customer: 'cu_supp_s_goms_customer',
    };

    return aliases[normalizedToken] || aliases[displayLabelToken] || normalizedValue;
  }

  private resolveSelectedDropdownValue(dropdown: any, rawValue: any): string {
    const normalizedRawValue = this.normalizeRedirectParamValue(rawValue);
    if (!normalizedRawValue) {
      return '';
    }

    const selectedOption = this.getSelectedDropdownOption(dropdown, rawValue);
    if (selectedOption) {
      return this.normalizeRedirectParamValue(this.getDropdownOptionValue(dropdown, selectedOption));
    }

    const optionsList = Array.isArray(dropdown?.optionsList) ? dropdown.optionsList : [];
    const normalizedRawValueLower = normalizedRawValue.toLowerCase();
    const matchedByLabel = optionsList.find((option: any) => {
      const optionLabel = this.normalizeRedirectParamValue(this.getDropdownOptionLabel(dropdown, option));
      return optionLabel.toLowerCase() === normalizedRawValueLower;
    });

    if (matchedByLabel) {
      return this.normalizeRedirectParamValue(this.getDropdownOptionValue(dropdown, matchedByLabel));
    }

    return normalizedRawValue;
  }

  private getRedirectMatchCandidates(
    dropdown: any,
    selectedValue: any,
    selectedLabel: string,
    ignoreCase: boolean,
  ): { value: Set<string>; label: Set<string> } {
    const selectedOption = this.getSelectedDropdownOption(dropdown, selectedValue);
    const valueCandidates = new Set<string>();
    const labelCandidates = new Set<string>();

    const addCandidate = (target: Set<string>, value: any): void => {
      const normalized = this.normalizeRedirectMatchValue(value, ignoreCase);
      if (normalized) {
        target.add(normalized);
      }
    };

    addCandidate(valueCandidates, selectedValue);
    addCandidate(labelCandidates, selectedLabel);

    if (selectedOption && typeof selectedOption === 'object') {
      addCandidate(valueCandidates, this.getDropdownOptionValue(dropdown, selectedOption));
      addCandidate(valueCandidates, selectedOption?.name);
      addCandidate(valueCandidates, selectedOption?.key);
      addCandidate(valueCandidates, selectedOption?.entity);

      addCandidate(labelCandidates, this.getDropdownOptionLabel(dropdown, selectedOption));
      addCandidate(labelCandidates, selectedOption?.display_name);
      addCandidate(labelCandidates, selectedOption?.displayName);
      addCandidate(labelCandidates, selectedOption?.label);
    }

    return {
      value: valueCandidates,
      label: labelCandidates,
    };
  }

  private normalizeColumnLabels(): void {
    if (!Array.isArray(this.data?.columns)) {
      return;
    }

    let hasChanges = false;
    const originalLabels = this.data.columns.map((column: any) => ({
      key: column?.key,
      label: column?.label,
    }));

    this.data.columns = this.data.columns.map((column: any) => {
      if (!column || typeof column !== 'object') {
        return column;
      }

      const normalizedLabel = this.getColumnDisplayLabel(column);
      if (normalizedLabel && normalizedLabel !== column.label) {
        hasChanges = true;
        return {
          ...column,
          label: normalizedLabel,
        };
      }

      return column;
    });

    if (hasChanges) {
      this.debugLog('column_labels_normalized', {
        before: originalLabels,
        after: this.data.columns.map((column: any) => ({
          key: column?.key,
          label: column?.label,
        })),
      });
    }
  }

  private filterHiddenColumns(): void {
    if (!Array.isArray(this.data?.columns)) {
      return;
    }

    const entityHiddenTokens = this.resolveEntityHiddenFieldTokens();
    const restrictToPaHistoryFields = RER_PA_HISTORY_ENTITIES.has(
      this.resolveSelectedEntityKey().trim().toLowerCase(),
    );

    this.data.columns = this.data.columns.filter((column: any) => {
      const candidates = [
        column?.key,
        column?.label,
        this.getColumnDisplayLabel(column),
      ];

      if (restrictToPaHistoryFields) {
        return candidates.some((candidate) =>
          EntityManagementComponent.RER_PA_HISTORY_FIELD_TOKENS.has(this.normalizeOrderToken(candidate)),
        );
      }

      if (candidates.some((candidate) => this.isHiddenFieldToken(candidate))) {
        return false;
      }

      if (
        entityHiddenTokens &&
        candidates.some((candidate) => entityHiddenTokens.has(this.normalizeOrderToken(candidate)))
      ) {
        return false;
      }

      return true;
    });
  }

  private resolveEntityHiddenFieldTokens(): Set<string> | null {
    const entityKey = this.resolveSelectedEntityKey();
    if (!entityKey) {
      return null;
    }

    return EntityManagementComponent.ENTITY_HIDDEN_FIELD_TOKENS.get(entityKey.toLowerCase()) ?? null;
  }

  protected override formatColumnLabel(value: string): string {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) {
      return '';
    }

    return normalizedValue
      .replace(/_/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .replace(/\w\S*/g, (text) => text.charAt(0).toUpperCase() + text.slice(1).toLowerCase())
      .replace(/\bHp\b/g, 'HP');
  }

  private shouldPreserveDropdownLabel(dropdown: any): boolean {
    const normalizedKey = String(dropdown?.key || '')
      .trim()
      .toLowerCase();

    return normalizedKey === 'entity';
  }

  private rewriteAttributeLabelString(value: string): string {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) {
      return '';
    }

    const hyphenMatch = normalizedValue.match(/^([^{}]+?)\s+-\s+([^{}]+)$/);
    if (hyphenMatch) {
      const left = hyphenMatch[1]?.trim();
      const right = hyphenMatch[2]?.trim();
      if (left && right) {
        return `${left}{${right}}`;
      }
    }

    return normalizedValue;
  }

  private formatDecimalCellValue(value: any): string | null {
    if (typeof value === 'number') {
      return Number.isInteger(value) ? null : value.toFixed(2);
    }

    if (typeof value !== 'string') {
      return null;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue || !this.isPlainNumericString(trimmedValue)) {
      return null;
    }

    const parsedValue = Number(trimmedValue);
    if (!Number.isFinite(parsedValue) || Number.isInteger(parsedValue)) {
      return null;
    }

    return parsedValue.toFixed(2);
  }

  private isPlainNumericString(value: string): boolean {
    return /^-?\d+(?:\.\d+)?$/.test(value);
  }

  private loadHeaderOrder(entityKey: string): Observable<string[] | null> {
    const normalizedEntityKey = String(entityKey || '').trim();
    const inlineOrder = this.resolveInlineHeaderOrder(normalizedEntityKey);
    if (inlineOrder?.length) {
      return of(inlineOrder);
    }

    if (this.data?.headerOrderLookup === false ||
      EntityManagementComponent.GOMS_CUSTOMER_ENTITY_KEYS.has(normalizedEntityKey.toLowerCase())) {
      return of(null);
    }

    const assetEntityKey = this.resolveHeaderOrderAssetEntityKey(normalizedEntityKey);
    if (!assetEntityKey) {
      return of(null);
    }

    const cachedOrder = this.headerOrderCache.get(assetEntityKey);
    if (cachedOrder) {
      return cachedOrder;
    }

    const orderRequest = this.http
      .get<any>(`assets/header-attribute/${assetEntityKey}.json`)
      .pipe(
        map((config) => this.parseHeaderOrder(config)),
        catchError(() => of(null)),
        shareReplay(1),
      );
    this.headerOrderCache.set(assetEntityKey, orderRequest);
    return orderRequest;
  }

  private resolveHeaderOrderAssetEntityKey(entityKey: string): string {
    const normalizedEntityKey = String(entityKey || '').trim().toLowerCase();
    const aliases: Record<string, string> = {
      custom_rer_engine_pricing: 'cu_rer_engine_pricing',
      custom_rer_suggested: 'cu_suggested_prices_rer_engine',
    };
    return aliases[normalizedEntityKey] || normalizedEntityKey;
  }

  private resolveInlineHeaderOrder(entityKey: string): string[] | null {
    const orderByEntity = this.data?.columnOrderByEntity;
    const normalizedEntityKey = String(entityKey || '').trim().toLowerCase();
    if (normalizedEntityKey && orderByEntity && typeof orderByEntity === 'object' && !Array.isArray(orderByEntity)) {
      const matchedOrder = Object.entries(orderByEntity).find(
        ([configuredEntity]) => String(configuredEntity || '').trim().toLowerCase() === normalizedEntityKey,
      )?.[1];
      const entityOrder = this.parseHeaderOrder(matchedOrder);
      if (entityOrder?.length) {
        return entityOrder;
      }
    }

    return this.parseHeaderOrder(this.data);
  }

  private parseHeaderOrder(config: any): string[] | null {
    const configuredOrder = Array.isArray(config)
      ? config
      : config?.columnOrder ?? config?.fieldOrder ?? config?.order;

    if (!Array.isArray(configuredOrder)) {
      return null;
    }

    const normalizedOrder = configuredOrder
      .map((value: any) => String(value || '').trim())
      .filter(Boolean);

    return normalizedOrder.length ? normalizedOrder : null;
  }

  private applyConfiguredColumnOrder(configuredOrder: string[] | null): void {
    if (!configuredOrder?.length || !Array.isArray(this.data?.columns)) {
      return;
    }

    const orderIndex = new Map<string, number>();
    configuredOrder.forEach((columnKey, index) => {
      orderIndex.set(this.normalizeOrderToken(columnKey), index);
    });

    const existingColumns = [...this.data.columns];
    this.data.columns = existingColumns.sort((left: any, right: any) => {
      const leftIndex = this.resolveConfiguredOrderIndex(left, orderIndex);
      const rightIndex = this.resolveConfiguredOrderIndex(right, orderIndex);

      if (leftIndex !== undefined && rightIndex !== undefined) {
        return leftIndex - rightIndex;
      }

      if (leftIndex !== undefined) {
        return -1;
      }

      if (rightIndex !== undefined) {
        return 1;
      }

      return 0;
    });
  }

  private applyKnownColumnOrderFallback(): void {
    if (!Array.isArray(this.data?.columns) || !this.data.columns.length) {
      return;
    }

    const selectedEntity = this.resolveSelectedEntityKey().trim().toLowerCase();
    if (!RER_PA_HISTORY_ENTITIES.has(selectedEntity)) {
      return;
    }

    this.data.columns = [...this.data.columns].sort((left: any, right: any) => {
      const leftOrder = this.resolveKnownColumnOrderIndex(left);
      const rightOrder = this.resolveKnownColumnOrderIndex(right);

      if (leftOrder !== undefined && rightOrder !== undefined) {
        return leftOrder - rightOrder;
      }

      if (leftOrder !== undefined) {
        return -1;
      }

      if (rightOrder !== undefined) {
        return 1;
      }

      return 0;
    });
  }

  private resolveConfiguredOrderIndex(column: any, orderIndex: Map<string, number>): number | undefined {
    const candidates = [
      column?.key,
      column?.label,
      this.getColumnDisplayLabel(column),
    ];

    for (const candidate of candidates) {
      const normalizedCandidate = this.normalizeOrderToken(candidate);
      if (!normalizedCandidate) {
        continue;
      }

      const matchedIndex = orderIndex.get(normalizedCandidate);
      if (matchedIndex !== undefined) {
        return matchedIndex;
      }
    }

    return undefined;
  }

  private resolveKnownColumnOrderIndex(column: any): number | undefined {
    const candidates = [
      column?.label,
      column?.key,
      this.getColumnDisplayLabel(column),
    ];

    for (const candidate of candidates) {
      const normalizedCandidate = this.normalizeOrderToken(candidate);
      if (!normalizedCandidate) {
        continue;
      }

      const matchedOrder = EntityManagementComponent.RER_PA_HISTORY_COLUMN_ORDER.get(normalizedCandidate);
      if (matchedOrder !== undefined) {
        return matchedOrder;
      }
    }

    return undefined;
  }

  private normalizeOrderToken(value: any): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ');
  }

  private isHiddenFieldToken(value: any): boolean {
    const normalizedValue = this.normalizeOrderToken(value);
    if (EntityManagementComponent.ALWAYS_HIDDEN_FIELD_TOKENS.has(normalizedValue)) {
      return true;
    }

    if (this.showSystemAttribute) {
      return false; // Show them if the toggle is checked
    }
    return EntityManagementComponent.HIDDEN_FIELD_TOKENS.has(normalizedValue);
  }

  private publishVisibleColumnsSchema(): void {
    if (!this.data?.widgetId || !Array.isArray(this.data?.columns)) {
      return;
    }

    const schema = this.data.columns
      .filter((column: any) => column?.key)
      .map((column: any) => ({
        key: String(column.key).trim(),
        label: this.getColumnDisplayLabel(column),
      }))
      .filter((column: any) => column.key);

    this.customGlobalFilterService.updateFilterSelection(
      `${this.data.widgetId}${EntityManagementComponent.COLUMN_SCHEMA_SUFFIX}`,
      schema,
      false,
      this.data.widgetId,
    );
  }

  private attachEyeLoopDebugListeners(): void {
    this.detachEyeLoopDebugListeners();

    this.eyeLoopErrorListener = ((event: Event) => {
      const errorEvent = event as ErrorEvent;
      this.eyeLoopDebug('window:error', {
        message: errorEvent.message,
        filename: errorEvent.filename,
        lineno: errorEvent.lineno,
        colno: errorEvent.colno,
        error: this.serializeEyeLoopError(errorEvent.error),
      });
    }) as EventListener;

    this.eyeLoopUnhandledRejectionListener = ((event: Event) => {
      const rejectionEvent = event as PromiseRejectionEvent;
      this.eyeLoopDebug('window:unhandledrejection', {
        reason: this.serializeEyeLoopError(rejectionEvent.reason),
      });
    }) as EventListener;

    window.addEventListener('error', this.eyeLoopErrorListener);
    window.addEventListener('unhandledrejection', this.eyeLoopUnhandledRejectionListener);
  }

  private detachEyeLoopDebugListeners(): void {
    if (this.eyeLoopErrorListener) {
      window.removeEventListener('error', this.eyeLoopErrorListener);
      this.eyeLoopErrorListener = undefined;
    }

    if (this.eyeLoopUnhandledRejectionListener) {
      window.removeEventListener('unhandledrejection', this.eyeLoopUnhandledRejectionListener);
      this.eyeLoopUnhandledRejectionListener = undefined;
    }
  }

  private eyeLoopDebug(event: string, payload?: any): void {
    this.eyeLoopDebugCounter += 1;
    console.log(`[${EntityManagementComponent.EYE_LOOP_DEBUG_KEY}] #${this.eyeLoopDebugCounter} ${event}`, {
      atMs: Math.round(performance.now()),
      payload,
    });
  }

  private serializeEyeLoopError(error: any): any {
    if (!error) {
      return error;
    }

    return {
      name: error?.name,
      message: error?.message || String(error),
      status: error?.status,
      statusText: error?.statusText,
      url: error?.url,
      stack: typeof error?.stack === 'string' ? error.stack.split('\n').slice(0, 12).join('\n') : undefined,
    };
  }

  private serializeEyeLoopPayload(payload: any): string {
    try {
      return JSON.stringify(payload);
    } catch {
      return String(payload);
    }
  }

  private getCompactEyeLoopStack(): string {
    const stack = new Error().stack || '';
    return stack
      .split('\n')
      .slice(2, 8)
      .map((line) => line.trim())
      .join(' | ');
  }

  private debugLog(event: string, payload: any): void {
    console.log(`[${EntityManagementComponent.DEBUG_LOG_KEY}] ${event}`, payload);
  }

  private attachTableRefreshListener(): void {
    this.tableRefreshEventListener = ((event: Event) => {
      const customEvent = event as CustomEvent<any>;
      const detail = customEvent?.detail;
      const widgetId = String(detail?.widgetId || '').trim();
      if (!widgetId || widgetId !== this.data?.widgetId) {
        return;
      }

      if (detail?.phase === 'prepare') {
        const appliedFilterPayload = (this as any).appliedDialogFilterPayload;
        const isRerEnginePricing =
          this.resolveSelectedEntityKey().trim().toLowerCase() === 'custom_rer_engine_pricing';
        if (this.hasDialogFilterApplied && appliedFilterPayload) {
          // The side form's configured post-save refresh payload is intentionally
          // replaceable. Reuse the table's active payload so saving a row cannot
          // turn a filtered table into an unfiltered one.
          if (isRerEnginePricing) {
            this.ensureRerEnginePricingActiveFilters(appliedFilterPayload, true);
          }
          detail.requestData = this.cloneCellActionPayload(appliedFilterPayload);
          this.debugLog('custom_refresh_filter_preserved', {
            widgetId,
            requestData: detail.requestData,
          });
        } else if (isRerEnginePricing) {
          // The configured post-save refresh request does not pass through
          // loadRecords(), so normalize it before the side form sends the API.
          detail.requestData = this.cloneCellActionPayload(detail.requestData);
          this.ensureRerEnginePricingActiveFilters(detail.requestData);
        }
        return;
      }

      if (detail?.response) {
        this.debugLog('custom_refresh_response_received', {
          widgetId,
          response: detail.response,
          saveContext: detail?.saveContext,
        });
        this.applyRefreshResponse(detail.response, detail?.saveContext);
        return;
      }

      if (detail?.requestData && typeof detail.requestData === 'object') {
        (this as any).pendingDialogRequestData = detail.requestData;
      }

      this.debugLog('custom_refresh_event_received', {
        widgetId,
        requestData: detail?.requestData,
      });
      this.loadRecords().subscribe();
    }) as EventListener;

    window.addEventListener(
      EntityManagementComponent.TABLE_REFRESH_EVENT,
      this.tableRefreshEventListener,
    );
  }

  private detachTableRefreshListener(): void {
    if (!this.tableRefreshEventListener) {
      return;
    }

    window.removeEventListener(
      EntityManagementComponent.TABLE_REFRESH_EVENT,
      this.tableRefreshEventListener,
    );
    this.tableRefreshEventListener = undefined;
  }

  private attachFormSchemaListener(): void {
    this.detachFormSchemaListener();

    this.formSchemaEventListener = ((event: Event) => {
      const detail = (event as CustomEvent<any>)?.detail;
      const targetWidgetIds = Array.isArray(detail?.targetWidgetIds) ? detail.targetWidgetIds : [];
      if (!this.data?.widgetId || !targetWidgetIds.includes(this.data.widgetId)) {
        return;
      }

      const eventEntity = String(detail?.entity || '').trim().toLowerCase();
      const selectedEntity = this.resolveSelectedEntityKey().trim().toLowerCase();
      if (eventEntity && selectedEntity && eventEntity !== selectedEntity) {
        return;
      }

      this.applyDomainSchema(detail?.domainFieldKeys, detail?.domainOptions);
      this.appendMissingFormColumns(detail?.fields);
    }) as EventListener;

    window.addEventListener(EntityManagementComponent.FORM_SCHEMA_EVENT, this.formSchemaEventListener);
  }

  private detachFormSchemaListener(): void {
    if (!this.formSchemaEventListener) {
      return;
    }

    window.removeEventListener(EntityManagementComponent.FORM_SCHEMA_EVENT, this.formSchemaEventListener);
    this.formSchemaEventListener = undefined;
  }

  private appendMissingFormColumns(fields: any): void {
    if (!Array.isArray(fields) || fields.length === 0) {
      return;
    }

    const columns = Array.isArray(this.data?.columns) ? this.data.columns : [];
    const existingKeys = new Set(columns.map((column: any) => this.normalizeSchemaKey(column?.key)));
    const missingColumns = fields
      .map((field: any) => ({
        key: String(field?.key || '').trim(),
        label: String(field?.label || '').trim(),
      }))
      .filter((field: any) => field.key && field.key !== 'id')
      .filter((field: any) => !this.isHiddenFieldToken(field.key) && !this.isHiddenFieldToken(field.label))
      .filter((field: any) => {
        const normalizedKey = this.normalizeSchemaKey(field.key);
        if (!normalizedKey || existingKeys.has(normalizedKey)) {
          return false;
        }
        existingKeys.add(normalizedKey);
        return true;
      })
      .map((field: any) => ({
        key: field.key,
        label: field.label || this.formatColumnLabel(field.key),
        sortable: true,
      }));

    if (missingColumns.length === 0) {
      return;
    }

    this.data.columns = [...columns, ...missingColumns];
    missingColumns.forEach(() => this.columnWidths.push(150));
    this.filterHiddenColumns();
    this.normalizeColumnLabels();
    this.loadHeaderOrder(this.resolveSelectedEntityKey()).subscribe((configuredOrder) => {
      if (configuredOrder?.length) {
        this.applyConfiguredColumnOrder(configuredOrder);
      } else {
        this.applyPreferredColumnOrder();
      }
      this.applyKnownColumnOrderFallback();
      this.publishVisibleColumnsSchema();
      this.customCdr.detectChanges();
    });
  }

  private applyDomainSchema(domainFieldKeys: any, domainOptions: any): void {
    this.domainAttributeColumnKeys = new Set(
      (Array.isArray(domainFieldKeys) ? domainFieldKeys : [])
        .map((key: any) => this.normalizeSchemaKey(key))
        .filter(Boolean),
    );
    this.domainValueLookupByColumn = new Map<
      string,
      Map<string, { code: string; name: string }>
    >();

    if (!domainOptions || typeof domainOptions !== 'object') {
      return;
    }

    Object.entries(domainOptions).forEach(([fieldKey, options]) => {
      const normalizedFieldKey = this.normalizeSchemaKey(fieldKey);
      if (!normalizedFieldKey || !this.domainAttributeColumnKeys.has(normalizedFieldKey) || !Array.isArray(options)) {
        return;
      }

      const lookup = new Map<string, { code: string; name: string }>();
      options.forEach((option: any) => {
        const data = option?.data ?? option;
        const code = data?.code ?? data?.Code ?? option?.code ?? option?.Code;
        if (code === undefined || code === null || code === '') {
          return;
        }
        const rawName = data?.name ?? data?.display_name ?? data?.displayName ??
          option?.name ?? option?.display_name ?? option?.displayName ?? option?.label ?? '';
        let name = String(rawName).trim();
        let candidates = this.getDomainLookupCandidates(option);
        if (normalizedFieldKey === 'oem_flag' && EntityManagementComponent.GOMS_CUSTOMER_ENTITY_KEYS.has(
          this.resolveSelectedEntityKey().trim().toLowerCase(),
        )) {
          // The form saves the OEM code in braces, not the domain row's ID.
          // An ID can equal another option's OEM code (e.g. Yes=1, review ID=1).
          const embeddedCodeMatch = String(code).match(/\{\s*(-?\d+)\s*\}\s*$/);
          if (embeddedCodeMatch && name === String(code).trim()) {
            name = '';
          }
          const oemLabel = name ? `${code}{${name}}` : String(code);
          const oemCodeMatch = embeddedCodeMatch || oemLabel.match(/\{\s*(-?\d+)\s*\}\s*$/);
          if (oemCodeMatch) {
            candidates = [
              this.normalizeDomainLookupValue(code),
              this.normalizeDomainLookupValue(oemLabel),
              String(Number(oemCodeMatch[1])),
            ];
          }
        }
        candidates.forEach((candidate) => lookup.set(candidate, {
          code: String(code),
          name,
        }));
      });
      this.domainValueLookupByColumn.set(normalizedFieldKey, lookup);
    });
  }

  private normalizeSchemaKey(value: any): string {
    return String(value || '').trim().toLowerCase();
  }

  private applyPreferredColumnOrder(): void {
    if (!Array.isArray(this.data?.columns) || this.data.columns.length < 2) {
      return;
    }

    const orderIndex = new Map(
      EntityManagementComponent.PREFERRED_COLUMN_ORDER.map((column, index) => [column, index]),
    );
    this.data.columns = this.data.columns
      .map((column: any, originalIndex: number) => ({ column, originalIndex }))
      .sort((left: any, right: any) => {
        const leftOrder = this.resolvePreferredColumnOrderIndex(left.column, orderIndex);
        const rightOrder = this.resolvePreferredColumnOrderIndex(right.column, orderIndex);
        if (leftOrder !== undefined && rightOrder !== undefined) {
          return leftOrder - rightOrder;
        }
        if (leftOrder !== undefined) {
          return -1;
        }
        if (rightOrder !== undefined) {
          return 1;
        }
        return left.originalIndex - right.originalIndex;
      })
      .map((entry: any) => entry.column);
  }

  private resolvePreferredColumnOrderIndex(column: any, orderIndex: Map<string, number>): number | undefined {
    for (const candidate of [column?.key, column?.label, this.getColumnDisplayLabel(column)]) {
      let token = this.normalizeOrderToken(candidate).replace(/\s*\/\s*/g, ' ');
      if (token === 'approval status') {
        token = 'approvals status';
      }
      const index = orderIndex.get(token);
      if (index !== undefined) {
        return index;
      }
    }
    return undefined;
  }

  private attachQuoteFormPopupListener(): void {
    this.quoteFormPopupEventListener = ((event: Event) => {
      const detail = (event as CustomEvent<any>)?.detail;
      if (String(detail?.widgetId || '').trim() !== String(this.data?.widgetId || '').trim()) {
        return;
      }

      const record = detail?.record;
      const fieldKey = String(detail?.fieldKey || '').trim();
      if (!record || !fieldKey) {
        return;
      }

      const clickEvent = new MouseEvent('click');
      if (detail?.type === 'approval-status') {
        this.openApprovalStatusDialog(clickEvent, record, { key: fieldKey, label: 'Approval Status' });
        return;
      }

      const actionIdByType: Record<string, string> = {
        'bill-to-ship-to': 'sap_bill_to_code_ship_to_details',
        'quote-sap-config': 'quote_sap_config_details',
        'ship-to-code-customer-name': 'ship_to_code_customer_name_details',
        'intercompany-configurations': 'intercompany_configurations_details',
      };
      const actionId = actionIdByType[String(detail?.type || '')];
      const action = (Array.isArray(this.data?.cellActions) ? this.data.cellActions : [])
        .find((candidate: any) => candidate?.actionId === actionId);
      if (action) {
        const popupAction = detail?.saveLocally === true
          ? { ...action, saveLocally: true, localFormFieldKey: fieldKey }
          : action;
        this.openCellActionDialog(clickEvent, record, { key: fieldKey, label: fieldKey }, popupAction);
      }
    }) as EventListener;

    window.addEventListener(
      EntityManagementComponent.QUOTE_FORM_POPUP_EVENT,
      this.quoteFormPopupEventListener,
    );
  }

  private detachQuoteFormPopupListener(): void {
    if (!this.quoteFormPopupEventListener) {
      return;
    }

    window.removeEventListener(
      EntityManagementComponent.QUOTE_FORM_POPUP_EVENT,
      this.quoteFormPopupEventListener,
    );
    this.quoteFormPopupEventListener = undefined;
  }

  private applyRefreshResponse(
    response: any,
    saveContext?: { isNewRecord?: boolean; recordSnapshot?: any },
  ): void {
    this.rememberLastSavedRecord(saveContext);
    this.rememberLastCreatedRecord(saveContext);
    const refreshedRecords = this.mergeSavedSnapshotIntoRefreshRecords(
      this.extractRecordsFromRefreshResponse(response),
      saveContext,
    );
    const recordsBeforeRefresh = Array.isArray(this.records) ? [...this.records] : [];
    const selectedRecordId = this.selectedRecord?.id ?? null;
    const previousSelectedRecord = this.selectedRecord;

    this.records = this.mergeQuoteDetailSaveIntoCurrentRecords(refreshedRecords, saveContext);
    if (saveContext?.isNewRecord === false) {
      // A refresh response may use a different server-side order after an
      // update (for example, when updated_date changes). Keep every surviving
      // row in the exact position it occupied before the save.
      this.records = this.preserveExistingRecordOrder(this.records, recordsBeforeRefresh);
    } else if (this.hasActiveSort()) {
      this.applyConsistentRecordOrdering();
    }
    // Keep the just-created RER pricing row at the top even when the refresh
    // response does not honor its requested `created_date desc` ordering.
    // Run this after client-side sorting so the saved row remains visible first.
    this.ensureSavedNewRecordIsVisible(saveContext);

    if (!this.data?.columns && this.records.length > 0) {
      const excludedColumns = new Set(
        (Array.isArray(this.data?.excludeColumns) ? this.data.excludeColumns : []).map((item: any) => String(item).trim()),
      );
      const firstRecord = this.records[0];
      this.data.columns = Object.keys(firstRecord)
        .filter((columnKey) => columnKey !== 'id' && !excludedColumns.has(columnKey))
        .map((columnKey) => ({
          key: columnKey,
          label: this.formatColumnLabel(columnKey),
          sortable: true,
        }));
    }

    this.loadHeaderOrder(this.resolveSelectedEntityKey()).subscribe((configuredOrder: string[] | null) => {
      this.columnWidths[0] = EntityManagementComponent.SELECTION_COLUMN_WIDTH;
      this.ensureQuoteApprovalStatusColumn();
      this.ensureRerEnginePricingSuggestedFuturePriceColumn();
      this.filterHiddenColumns();
      this.normalizeColumnLabels();
      if (configuredOrder?.length) {
        this.applyConfiguredColumnOrder(configuredOrder);
      } else {
        this.applyPreferredColumnOrder();
      }
      this.applyKnownColumnOrderFallback();

      if (this.records.length > 0) {
        const preferredRecord =
          this.resolveRecordBySnapshot(saveContext?.recordSnapshot) ||
          this.resolveRecordBySnapshot(previousSelectedRecord) ||
          this.resolveRecordBySelectedId(selectedRecordId) ||
          this.resolveRecordBySnapshot(this.lastSavedRecordSnapshot) ||
          this.records[0];
        this.selectRecord(preferredRecord);
      } else {
        this.selectRecord(null);
      }

      this.loading = false;
      if (this.data?.widgetId) {
        this.customGlobalFilterService.updateFilterSelection('loading', [this.loading], false, this.data.widgetId);
      }
    });
  }

  private mergeQuoteDetailSaveIntoCurrentRecords(
    refreshedRecords: any[],
    saveContext?: { isNewRecord?: boolean; recordSnapshot?: any },
  ): any[] {
    const selectedEntity = String(this.resolveSelectedEntityKey() || '').trim().toLowerCase();
    const snapshot = saveContext?.recordSnapshot;
    if (
      selectedEntity !== EntityManagementComponent.QUOTE_DETAIL_ENTITY ||
      saveContext?.isNewRecord !== false ||
      !snapshot ||
      !Array.isArray(this.records)
    ) {
      return refreshedRecords;
    }

    const currentRecordIndex = this.findRecordIndexBySnapshot(this.records, snapshot);
    if (currentRecordIndex < 0) {
      return this.records;
    }

    const refreshedRecordIndex = this.findRecordIndexBySnapshot(refreshedRecords, snapshot);
    const refreshedRecord = refreshedRecordIndex >= 0
      ? refreshedRecords[refreshedRecordIndex]
      : snapshot;

    return this.records.map((record: any, index: number) => index === currentRecordIndex
      ? { ...record, ...refreshedRecord }
      : record);
  }

  private mergeSavedSnapshotIntoRefreshRecords(
    records: any[],
    saveContext?: { isNewRecord?: boolean; recordSnapshot?: any },
  ): any[] {
    const snapshot = saveContext?.recordSnapshot;
    if (!Array.isArray(records) || !snapshot || typeof snapshot !== 'object') {
      return records;
    }

    const matchedRecordIndex = this.findRecordIndexBySnapshot(records, snapshot);
    if (matchedRecordIndex < 0) {
      return records;
    }

    return records.map((record: any, index: number) => index === matchedRecordIndex
      ? { ...record, ...snapshot }
      : record);
  }

  private preserveExistingRecordOrder(refreshedRecords: any[], previousRecords: any[]): any[] {
    if (
      !Array.isArray(refreshedRecords) ||
      !Array.isArray(previousRecords) ||
      refreshedRecords.length < 2 ||
      previousRecords.length === 0
    ) {
      return refreshedRecords;
    }

    const remainingRecords = [...refreshedRecords];
    const orderedRecords: any[] = [];

    previousRecords.forEach((previousRecord: any) => {
      const previousIdentities = this.getSaveIdentityCandidates(previousRecord);
      if (previousIdentities.length === 0) {
        return;
      }

      const refreshedIndex = remainingRecords.findIndex((record: any) =>
        this.recordsMatchSnapshot(record, previousRecord, previousIdentities),
      );
      if (refreshedIndex >= 0) {
        orderedRecords.push(remainingRecords.splice(refreshedIndex, 1)[0]);
      }
    });

    // Rows not present in the previous view are retained after the known rows.
    return [...orderedRecords, ...remainingRecords];
  }

  private extractRecordsFromRefreshResponse(response: any): any[] {
    const key = this.data?.key || null;
    const keyedRows = this.extractRecordsFromResponse(response, key);
    if (keyedRows.length > 0) {
      return keyedRows;
    }

    const result = key ? response?.[key] : response;
    const responseList = Array.isArray(response?.responseList) ? response.responseList : [];
    const firstResponseEntry = responseList[0];
    const firstResponseData = firstResponseEntry?.data;

    const candidates = [
      result,
      result?.data,
      response?.data,
      key ? firstResponseEntry?.[key] : null,
      key ? firstResponseEntry?.[key]?.map((item: any) => item?.data ?? item) : null,
      Array.isArray(firstResponseData) ? firstResponseData[0]?.data : null,
      firstResponseData,
      Array.isArray(firstResponseData) ? firstResponseData[0] : null,
    ];

    for (const candidate of candidates) {
      if (this.isMeaningfulRecordArray(candidate)) {
        return this.unwrapRefreshRecordDataRows(candidate);
      }
    }

    return [];
  }

  private unwrapRefreshRecordDataRows(records: any[]): any[] {
    return records.map((record: any) => {
      if (
        record &&
        typeof record === 'object' &&
        record.data &&
        typeof record.data === 'object' &&
        !Array.isArray(record.data) &&
        Object.keys(record).length === 1
      ) {
        return record.data;
      }

      return record;
    });
  }

  private rememberLastCreatedRecord(
    saveContext?: { isNewRecord?: boolean; recordSnapshot?: any },
  ): void {
    if (!saveContext?.isNewRecord || !saveContext?.recordSnapshot) {
      return;
    }

    this.lastCreatedRecordSnapshot = {
      ...saveContext.recordSnapshot,
    };
    this.persistLastCreatedRecordSnapshot();
  }

  private rememberLastSavedRecord(
    saveContext?: { isNewRecord?: boolean; recordSnapshot?: any },
  ): void {
    if (!saveContext?.recordSnapshot) {
      return;
    }

    this.lastSavedRecordSnapshot = {
      ...saveContext.recordSnapshot,
    };
  }

  private resolveRecordBySnapshot(snapshot: any): any | null {
    if (!snapshot || !Array.isArray(this.records) || this.records.length === 0) {
      return null;
    }

    const matchedRecordIndex = this.findRecordIndexBySnapshot(this.records, snapshot);
    return matchedRecordIndex >= 0 ? this.records[matchedRecordIndex] : null;
  }

  private resolveRecordBySelectedId(selectedRecordId: any): any | null {
    if (selectedRecordId === null || selectedRecordId === undefined || !Array.isArray(this.records)) {
      return null;
    }

    return this.records.find((record: any) => record?.id === selectedRecordId) || null;
  }

  private persistLastCreatedRecordSnapshot(): void {
    const storageKey = this.getLastCreatedRecordStorageKey();
    if (!storageKey || !this.lastCreatedRecordSnapshot) {
      return;
    }

    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify(this.lastCreatedRecordSnapshot));
    } catch {
      // Ignore storage failures and keep the in-memory snapshot.
    }
  }

  private restoreLastCreatedRecordSnapshot(): void {
    if (this.lastCreatedRecordSnapshot) {
      return;
    }

    const storageKey = this.getLastCreatedRecordStorageKey();
    if (!storageKey) {
      return;
    }

    try {
      const storedValue = window.sessionStorage.getItem(storageKey);
      if (!storedValue) {
        return;
      }

      const parsedSnapshot = JSON.parse(storedValue);
      if (parsedSnapshot && typeof parsedSnapshot === 'object') {
        this.lastCreatedRecordSnapshot = parsedSnapshot;
      }
    } catch {
      // Ignore malformed storage values.
    }
  }

  private getLastCreatedRecordStorageKey(): string {
    const widgetId = String(this.data?.widgetId || '').trim();
    if (!widgetId) {
      return '';
    }

    const entityKey = String(this.resolveSelectedEntityKey() || this.currentFilters?.entity || 'default').trim();
    return `${EntityManagementComponent.LAST_CREATED_RECORD_STORAGE_PREFIX}:${widgetId}:${entityKey || 'default'}`;
  }

  private ensureSavedNewRecordIsVisible(
    saveContext?: { isNewRecord?: boolean; recordSnapshot?: any },
    insertWhenMissing = true,
  ): void {
    if (!saveContext?.isNewRecord || !saveContext?.recordSnapshot || !Array.isArray(this.records)) {
      return;
    }

    const matchedRecordIndex = this.findRecordIndexBySnapshot(this.records, saveContext.recordSnapshot);
    if (matchedRecordIndex >= 0) {
      const selectedEntity = this.resolveSelectedEntityKey().trim().toLowerCase();
      if (
        matchedRecordIndex > 0 &&
        EntityManagementComponent.RER_NEWEST_FIRST_ENTITY_KEYS.has(selectedEntity)
      ) {
        const savedRecord = this.records[matchedRecordIndex];
        this.records = [
          savedRecord,
          ...this.records.slice(0, matchedRecordIndex),
          ...this.records.slice(matchedRecordIndex + 1),
        ];
      }
      return;
    }

    if (!insertWhenMissing) {
      return;
    }

    this.records = [
      {
        ...saveContext.recordSnapshot,
      },
      ...this.records,
    ];
  }

  private hasActiveSort(): boolean {
    return !!this.sortColumn;
  }

  private applyConsistentRecordOrdering(): void {
    if (!Array.isArray(this.records) || this.records.length < 2) {
      return;
    }

    const sortColumn = this.hasActiveSort()
      ? (this.sortColumn as string)
      : null;
    if (!sortColumn) {
      return;
    }

    const direction = this.hasActiveSort() && this.sortDirection === 'desc' ? -1 : 1;
    const column = this.data?.columns?.find((item: any) => item?.key === sortColumn);

    this.records = [...this.records].sort((left: any, right: any) =>
      this.compareCustomRecordValues(left?.[sortColumn], right?.[sortColumn], column) * direction,
    );
  }

  private compareCustomRecordValues(leftValue: any, rightValue: any, column?: any): number {
    const normalizedLeft = this.getCustomSortableCellValue(leftValue, column);
    const normalizedRight = this.getCustomSortableCellValue(rightValue, column);

    if (normalizedLeft === normalizedRight) {
      return 0;
    }

    if (normalizedLeft === '') {
      return -1;
    }

    if (normalizedRight === '') {
      return 1;
    }

    const numericLeft = Number(normalizedLeft);
    const numericRight = Number(normalizedRight);
    const bothNumeric =
      normalizedLeft.trim() !== '' &&
      normalizedRight.trim() !== '' &&
      !Number.isNaN(numericLeft) &&
      !Number.isNaN(numericRight);

    if (bothNumeric) {
      return numericLeft - numericRight;
    }

    const dateLeft = Date.parse(normalizedLeft);
    const dateRight = Date.parse(normalizedRight);
    const bothDates = !Number.isNaN(dateLeft) && !Number.isNaN(dateRight);
    if (bothDates) {
      return dateLeft - dateRight;
    }

    return normalizedLeft.localeCompare(normalizedRight, undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  }

  private getCustomSortableCellValue(value: any, column?: any): string {
    return this.formatCellValue(value, column).trim();
  }

  private findRecordIndexBySnapshot(records: any[], snapshot: any): number {
    if (!Array.isArray(records) || !snapshot || typeof snapshot !== 'object') {
      return -1;
    }

    const snapshotCandidates = this.getSaveIdentityCandidates(snapshot);
    if (snapshotCandidates.length > 0) {
      const matchedIndex = records.findIndex((record: any) => {
        return this.recordsMatchSnapshot(record, snapshot, snapshotCandidates);
      });
      if (matchedIndex >= 0) {
        return matchedIndex;
      }
    }

    return this.findRecordIndexByFieldSimilarity(records, snapshot);
  }

  private recordsMatchSnapshot(record: any, snapshot: any, snapshotCandidates?: string[]): boolean {
    const recordCandidates = this.getSaveIdentityCandidates(record);
    const comparisonCandidates = snapshotCandidates || this.getSaveIdentityCandidates(snapshot);
    return comparisonCandidates.some((candidate) => recordCandidates.includes(candidate));
  }

  private getSaveIdentityCandidates(record: any): string[] {
    if (!record || typeof record !== 'object') {
      return [];
    }

    return [
      record?.id,
      record?.uuid,
      record?.UUID,
      record?.code,
      record?.Code,
    ]
      .filter((value) => value !== null && value !== undefined && String(value).trim() !== '')
      .map((value) => String(value).trim());
  }

  private findRecordIndexByFieldSimilarity(records: any[], snapshot: any): number {
    const comparableEntries = Object.entries(snapshot || {}).filter(([key, value]) =>
      key !== '__isDraftNewRecord' &&
      value !== null &&
      value !== undefined &&
      String(value).trim() !== '' &&
      ['string', 'number', 'boolean'].includes(typeof value),
    );

    if (!comparableEntries.length) {
      return -1;
    }

    const minimumMatchCount = Math.min(comparableEntries.length, 2);
    let bestMatchIndex = -1;
    let bestMatchScore = 0;

    records.forEach((record: any, index: number) => {
      if (!record || typeof record !== 'object') {
        return;
      }

      const matchScore = comparableEntries.reduce((score, [key, value]) => {
        return this.normalizeComparableValue(record[key]) === this.normalizeComparableValue(value)
          ? score + 1
          : score;
      }, 0);

      if (matchScore > bestMatchScore) {
        bestMatchScore = matchScore;
        bestMatchIndex = index;
      }
    });

    return bestMatchScore >= minimumMatchCount ? bestMatchIndex : -1;
  }

  private normalizeComparableValue(value: any): string {
    return String(value ?? '')
      .trim()
      .toLowerCase();
  }

  private isMeaningfulRecordArray(candidate: any): candidate is any[] {
    if (!Array.isArray(candidate)) {
      return false;
    }

    if (candidate.length === 0) {
      return true;
    }

    return candidate.some((item: any) => {
      if (item === null || item === undefined) {
        return false;
      }

      if (typeof item !== 'object') {
        return true;
      }

      return Object.keys(item).length > 0;
    });
  }

  isPricingActionHistoryColumn(column: any): boolean {
    if (!column || typeof column !== 'object') {
      return false;
    }

    const normalizedKey = this.normalizeOrderToken(column?.key || '');
    const normalizedLabel = this.normalizeOrderToken(column?.label || this.getColumnDisplayLabel(column));

    return normalizedKey === 'pricing action history' ||
           normalizedLabel === 'pricing action history' ||
           normalizedKey === 'pricing history' ||
           normalizedLabel === 'pricing history';
  }

  onPricingActionIconClick(event: Event, _record: any): void {
    event.stopPropagation();

    // Open PA History in the Palantir table using its audit entity contract.
    const queryParams = { entity: RER_AUDIT_ENTITY };
    this.debugLog('pricing_action_history_navigate', { queryParams });
    void this.router.navigate(['/palantir_charts'], { queryParams });
  }

  /**
   * Reads a configuration value off the clicked row. The record keys come from
   * the API and may not match the target token exactly (case / underscores /
   * spaces), so we fall back to a normalized key lookup, then reduce the value
   * to a plain string (unwrapping `{ code }` objects and `code{label}` strings).
   */
  private resolvePricingConfigValue(record: any, targetKey: string): string | null {
    if (!record || typeof record !== 'object') {
      return null;
    }

    if (record[targetKey] != null) {
      return this.stringifyPricingConfigValue(record[targetKey]);
    }

    const normalizedTarget = this.normalizeConfigToken(targetKey);
    for (const [key, value] of Object.entries(record)) {
      if (this.normalizeConfigToken(key) === normalizedTarget) {
        return this.stringifyPricingConfigValue(value);
      }
    }
    return null;
  }

  private normalizeConfigToken(value: string): string {
    return String(value).toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private stringifyPricingConfigValue(value: any): string | null {
    if (value == null) {
      return null;
    }

    if (typeof value === 'object') {
      const candidate = value.code ?? value.value ?? value.id ?? value.name ?? value.label;
      return candidate != null ? this.stringifyPricingConfigValue(candidate) : null;
    }

    let str = String(value).trim();
    const braceIndex = str.indexOf('{');
    if (braceIndex > 0) {
      str = str.slice(0, braceIndex).trim();
    }
    return str || null;
  }
}
